 /**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 1
 * ============================================================
 *
 * Requires:
 * 1. rise-skin-products.js
 * 2. rise-skin-quiz.js
 * 3. rise-skin-quiz-ui.js
 * 4. rise-skin-engine.js
 *
 * This file:
 * - Builds routines from completed quiz profile
 * - Populates the existing Shopify results section
 * - Controls Essential / Complete / Elevated toggles
 * - Creates skin snapshot + primary priority
 * - Renders AM / PM routine steps
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// RESULTS STATE
// ============================================================

window.RISE_SKIN.RESULTS = {
  data: null,
  activeLevel: "complete"
};


// ============================================================
// HUMAN LABELS
// ============================================================

window.RISE_SKIN.concernLabels = {
  acne: "Breakouts + acne",
  pores: "Visible pores",
  texture: "Texture + roughness",
  pigment: "Dark spots + uneven tone",
  redness: "Redness",
  fineLines: "Fine lines + wrinkles",
  laxity: "Firmness + laxity",
  dullness: "Dullness + glow",
  dehydration: "Dryness + dehydration",
  underEye: "Under-eye concerns",
  prevention: "Preventative skin health"
};


window.RISE_SKIN.skinBehaviorLabels = {
  dry: "dry",
  balanced: "balanced",
  combination: "combination",
  oily: "oily",
  variable: "variable"
};


window.RISE_SKIN.sensitivityLabels = {
  resilient: "resilient",
  normal: "generally tolerant",
  sensitive: "sensitive",
  very_sensitive: "very sensitive"
};


// ============================================================
// PRIMARY CONCERN
// ============================================================

window.RISE_SKIN.getPrimaryConcern =
  function (profile) {

    const concerns =
      window.RISE_SKIN.asArray(
        profile.concerns
      );

    return concerns.length
      ? concerns[0]
      : null;
  };


// ============================================================
// PRIORITY DISPLAY
// ============================================================

window.RISE_SKIN.getPriorityDisplay =
  function (profile) {

    const primary =
      window.RISE_SKIN
        .getPrimaryConcern(profile);


    const displayMap = {
      acne: "Clear + Calm",
      pores: "Refine + Smooth",
      texture: "Smooth + Resurface",
      pigment: "Brighten + Even Tone",
      redness: "Calm + Strengthen",
      fine_lines: "Smooth + Prevent",
      fineLines: "Smooth + Prevent",
      laxity: "Firm + Support",
      dullness: "Brighten + Restore Glow",
      dehydration: "Hydrate + Repair",
      under_eye: "Refresh the Eye Area",
      underEye: "Refresh the Eye Area",
      prevention: "Protect + Prevent"
    };


    return (
      displayMap[primary] ||
      "Healthy, Balanced Skin"
    );
  };


// ============================================================
// SKIN SNAPSHOT
// ============================================================

window.RISE_SKIN.buildSkinSnapshot =
  function (profile) {

    const parts = [];


    // Skin type.
    if (profile.skinBehavior) {

      const label =
        window.RISE_SKIN
          .skinBehaviorLabels[
            profile.skinBehavior
          ];

      if (label) {
        parts.push(label);
      }
    }


    // Sensitivity.
    if (
      profile.sensitivity &&
      profile.sensitivity !== "normal"
    ) {

      const label =
        window.RISE_SKIN
          .sensitivityLabels[
            profile.sensitivity
          ];

      if (label) {
        parts.push(label);
      }
    }


    // Barrier.
    if (
      profile.barrierStatus === "healthy"
    ) {
      parts.push("stable barrier");
    }

    if (
      profile.barrierStatus === "mild"
    ) {
      parts.push("some barrier stress");
    }

    if (
      profile.barrierStatus === "impaired"
    ) {
      parts.push("currently compromised barrier");
    }


    // Acne.
    if (
      profile.acneSeverity &&
      profile.acneSeverity !== "rare"
    ) {

      const acneMap = {
        occasional: "occasional breakouts",
        congested: "congestion-prone",
        inflamed: "inflammatory breakouts",
        cystic: "deeper breakouts"
      };

      if (
        acneMap[
          profile.acneSeverity
        ]
      ) {
        parts.push(
          acneMap[
            profile.acneSeverity
          ]
        );
      }
    }


    // Sun.
    if (
      profile.sunExposure === "high" ||
      profile.sunExposure === "very_high"
    ) {
      parts.push("high UV exposure");
    }


    if (!parts.length) {
      return "Your skin profile is balanced and relatively uncomplicated.";
    }


    const first =
      parts.shift();


    let sentence =
      first.charAt(0).toUpperCase() +
      first.slice(1);


    if (parts.length) {
      sentence += " • " + parts.join(" • ");
    }


    return sentence;
  };


// ============================================================
// ROUTINE DESCRIPTION
// ============================================================

window.RISE_SKIN.getRoutineDescription =
  function (level) {

    const descriptions = {

      simple:
        "The highest-impact essentials for your goals—nothing extra.",

      complete:
        "Our ideal everyday routine for your skin, with a strong clinical foundation and personalized correction.",

      elevated:
        "Your complete routine plus advanced support where it meaningfully adds value."
    };


    return (
      descriptions[level] ||
      descriptions.complete
    );
  };


// ============================================================
// PRODUCT CARD
// ============================================================

window.RISE_SKIN.renderRoutineProduct =
  function (
    item,
    index
  ) {

    const product =
      item.product || {};


    return `
      <div
        class="rsc-product-card"
        data-rise-product-id="${product.id || ""}"
      >

        <div class="rsc-product-step">
          ${String(index + 1).padStart(2, "0")}
        </div>

        <div class="rsc-product-main">

          <div class="rsc-product-brand">
            ${product.brand || ""}
          </div>

          <h3 class="rsc-product-name">
            ${product.name || ""}
          </h3>

          ${
            item.reason
              ? `
                <p class="rsc-product-reason">
                  ${item.reason}
                </p>
              `
              : ""
          }

          <button
            type="button"
            class="rsc-text-link rise-product-more"
            data-rise-more-product="${product.id || ""}"
          >
            WHY THIS PRODUCT? →
          </button>

          <div
            class="rsc-more-info rise-product-more-box"
            data-rise-more-box="${product.id || ""}"
            style="display:none;"
          >
            ${window.RISE_SKIN.getProductWhyText(item)}
          </div>

        </div>

      </div>
    `;
  };


// ============================================================
// WHY THIS PRODUCT
// ============================================================

window.RISE_SKIN.getProductWhyText =
  function (item) {

    const product =
      item.product || {};

    const tags =
      window.RISE_SKIN.asArray(
        product.reasonTags
      );


    const phrases = [];


    const tagMap = {
      ACNE:
        "helps support clearer-looking skin",
      CONGESTION:
        "targets congestion and clogged pores",
      PORES:
        "supports a smoother, more refined appearance",
      TEXTURE:
        "helps improve uneven texture",
      PIGMENT:
        "supports brighter, more even-looking tone",
      REDNESS:
        "supports skin that is prone to visible redness",
      FINE_LINES:
        "supports smoother-looking fine lines",
      AGING:
        "supports healthy skin aging",
      DULLNESS:
        "helps restore visible radiance",
      HYDRATION:
        "helps replenish hydration",
      BARRIER:
        "supports the skin barrier",
      UNDER_EYE:
        "targets the delicate eye area",
      SPF:
        "provides daily UV protection",
      ANTIOXIDANT:
        "adds antioxidant protection against environmental stressors",
      REGENERATIVE:
        "adds advanced regenerative skin support"
    };


    tags.forEach(
      (tag) => {

        if (
          tagMap[tag] &&
          !phrases.includes(
            tagMap[tag]
          )
        ) {

          phrases.push(
            tagMap[tag]
          );
        }
      }
    );


    if (!phrases.length) {

      return (
        item.reason ||
        "This product scored highly for your skin profile and routine goals."
      );
    }


    const selected =
      phrases.slice(0, 3);


    return (
      "Recommended because it " +
      selected.join(", ") +
      "."
    );
  };


// ============================================================
// RENDER ROUTINE
// ============================================================

window.RISE_SKIN.renderRoutineLevel =
  function (level) {

    const result =
      window.RISE_SKIN.RESULTS.data;

    if (
      !result ||
      !result.routines ||
      !result.routines[level]
    ) {
      return;
    }


    window.RISE_SKIN.RESULTS
      .activeLevel =
      level;


    const routine =
      result.routines[level];


    // --------------------------------------------------------
    // TAB STATES
    // --------------------------------------------------------

    [
      "simple",
      "complete",
      "elevated"
    ].forEach(
      (tabLevel) => {

        const button =
          document.getElementById(
            `rsc-tab-${tabLevel}`
          );

        if (!button) {
          return;
        }


        if (tabLevel === level) {

          button.classList.add(
            "active"
          );

        } else {

          button.classList.remove(
            "active"
          );
        }
      }
    );


    // --------------------------------------------------------
    // DESCRIPTION
    // --------------------------------------------------------

    const description =
      document.getElementById(
        "rsc-routine-description"
      );


    if (description) {

      description.textContent =
        window.RISE_SKIN
          .getRoutineDescription(
            level
          );
    }


    // --------------------------------------------------------
    // AM
    // --------------------------------------------------------

    const am =
      document.getElementById(
        "rsc-am-routine"
      );


    if (am) {

      am.innerHTML =
        routine.am
          .map(
            (item, index) =>
              window.RISE_SKIN
                .renderRoutineProduct(
                  item,
                  index
                )
          )
          .join("");
    }


    // --------------------------------------------------------
    // PM
    // --------------------------------------------------------

    const pm =
      document.getElementById(
        "rsc-pm-routine"
      );


    if (pm) {

      pm.innerHTML =
        routine.pm
          .map(
            (item, index) =>
              window.RISE_SKIN
                .renderRoutineProduct(
                  item,
                  index
                )
          )
          .join("");
    }


    window.RISE_SKIN
      .bindProductMoreButtons();
  };


  // ============================================================
// BODY PLAN RENDERER
// ============================================================

window.RISE_SKIN.renderBodyPlan =
  function (result) {

    const section =
      document.getElementById(
        "rsc-body-section"
      );

    const container =
      document.getElementById(
        "rsc-body-products"
      );

    if (!section || !container) {
      return;
    }

    const recommendations =
      Array.isArray(
        result?.bodyRecommendations
      )
        ? result.bodyRecommendations
        : [];

    if (!recommendations.length) {
      section.style.display = "none";
      container.innerHTML = "";
      return;
    }

    container.innerHTML =
      recommendations
        .map(
          (item, index) =>
            window.RISE_SKIN
              .renderRoutineProduct(
                item,
                index
              )
        )
        .join("");

    section.style.display = "";
  };
  
// ============================================================
// PRODUCT MORE BUTTONS
// ============================================================

window.RISE_SKIN.bindProductMoreButtons =
  function () {

    document
      .querySelectorAll(
        "[data-rise-more-product]"
      )
      .forEach(
        (button) => {

          button.onclick =
            function () {

              const id =
                button.dataset
                  .riseMoreProduct;


              const box =
                document.querySelector(
                  `[data-rise-more-box="${id}"]`
                );


              if (!box) {
                return;
              }


              const isOpen =
                box.style.display !==
                "none";


              box.style.display =
                isOpen
                  ? "none"
                  : "block";


              button.textContent =
                isOpen
                  ? "WHY THIS PRODUCT? →"
                  : "CLOSE ↑";
            };
        }
      );
  };


// ============================================================
// HEADLINE PRODUCT
// ============================================================

window.RISE_SKIN.getHeadlineProduct =
  function (result) {

    const level =
      result.recommendedLevel ||
      "complete";


    const routine =
      result.routines[level] ||
      result.routines.complete;


    if (!routine) {
      return null;
    }


    const excludedFamilies = [
      "cleanser",
      "spf",
      "moisturizer"
    ];


    const candidates =
      [
        ...routine.am,
        ...routine.pm
      ]
      .filter(
        (item) =>
          !excludedFamilies.includes(
            item.family
          )
      )
      .sort(
        (a, b) =>
          b.score - a.score
      );


    return (
      candidates[0] ||
      null
    );
  };


// ============================================================
// POPULATE RESULTS
// ============================================================

window.RISE_SKIN.populateResults =
  function (result) {

    const profile =
      result.profile;


    // --------------------------------------------------------
    // SNAPSHOT
    // --------------------------------------------------------

    const snapshot =
      document.getElementById(
        "rsc-snapshot-text"
      );


    if (snapshot) {

      snapshot.textContent =
        window.RISE_SKIN
          .buildSkinSnapshot(
            profile
          );
    }


    // --------------------------------------------------------
    // PRIORITY
    // --------------------------------------------------------

    const priority =
      document.getElementById(
        "rsc-primary-goal"
      );


    if (priority) {

      priority.textContent =
        window.RISE_SKIN
          .getPriorityDisplay(
            profile
          );
    }


    // --------------------------------------------------------
    // HEADLINE PRODUCT
    // --------------------------------------------------------

    const headline =
      window.RISE_SKIN
        .getHeadlineProduct(
          result
        );


    const headlineName =
      document.getElementById(
        "rsc-headline-product"
      );


    const headlineReason =
      document.getElementById(
        "rsc-headline-reason"
      );


    const whyBox =
      document.getElementById(
        "rsc-why-box"
      );


    if (headline) {

      if (headlineName) {
        headlineName.textContent =
          headline.product.name;
      }


      if (headlineReason) {

        headlineReason.textContent =
          "This is one of the highest-impact additions for the goals you told us matter most.";
      }


      if (whyBox) {

        whyBox.textContent =
          window.RISE_SKIN
            .getProductWhyText(
              headline
            );
      }
    }
/* --------------------------------------------------------
   TREATMENT MATCHES
   -------------------------------------------------------- */

const treatmentPrimary =
  document.getElementById(
    "rsc-treatment-primary"
  );

const treatmentReason =
  document.getElementById(
    "rsc-treatment-primary-reason"
  );

const treatmentSecondary =
  document.getElementById(
    "rsc-treatment-secondary"
  );

const treatmentSection =
  document.getElementById(
    "rsc-treatment-section"
  );

const treatments =
  result?.treatments || null;


if (
  treatments &&
  treatments.primary
) {

  if (treatmentPrimary) {
    treatmentPrimary.textContent =
      treatments.primary.name || "";
  }

  if (treatmentReason) {
    treatmentReason.textContent =
      treatments.primary.reason || "";
  }

  if (treatmentSecondary) {
    treatmentSecondary.textContent =
      treatments.secondary?.name || "";
  }

  if (treatmentSection) {
    treatmentSection.style.display =
      "";
  }

} else {

  if (treatmentSection) {
    treatmentSection.style.display =
      "none";
  }
}

    // --------------------------------------------------------
    // DEFAULT ROUTINE LEVEL
    // --------------------------------------------------------

    const defaultLevel =
      [
        "simple",
        "complete",
        "elevated"
      ].includes(
        result.recommendedLevel
      )
        ? result.recommendedLevel
        : "complete";


    window.RISE_SKIN
      .renderRoutineLevel(
        defaultLevel
      );
  };


// ============================================================
// SHOW SCREEN
// ============================================================

window.RISE_SKIN.showConciergeScreen =
  function (screenId) {

    document
      .querySelectorAll(
        "#rise-skin-concierge .rsc-screen"
      )
      .forEach(
        (screen) =>
          screen.classList.remove(
            "active"
          )
      );


    const target =
      document.getElementById(
        screenId
      );


    if (target) {

      target.classList.add(
        "active"
      );
    }


    // The lead form is allowed only during the intentional
    // post-quiz BUILDING state. Any other Concierge screen
    // immediately re-locks and hides the form. This prevents
    // stale state from a previous test/session leaking into a
    // newly started quiz.
    if (
      window.RISE_SKIN.FORM_GATE &&
      screenId !== "rsc-building"
    ) {

      window.RISE_SKIN
        .FORM_GATE
        .shouldBeVisible =
        false;

      if (
        typeof window.RISE_SKIN
          .hideSkinConciergeForm ===
        "function"
      ) {
        window.RISE_SKIN
          .hideSkinConciergeForm();
      }
    }
  };


// ============================================================
// QUIZ COMPLETION BRIDGE
// ============================================================

window.RISE_SKIN.onQuizComplete =
  function (profile) {

    window.RISE_SKIN
      .showConciergeScreen(
        "rsc-building"
      );


    const result =
      window.RISE_SKIN
        .buildRoutines(
          profile
        );


    window.RISE_SKIN.RESULTS.data =
      result;


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN — RESULTS DATA"
      );

      console.log(result);

      console.groupEnd();
    }


    // Short intentional transition.
    window.setTimeout(
      function () {

        window.RISE_SKIN
          .populateResults(
            result
          );


        window.RISE_SKIN
          .showConciergeScreen(
            "rsc-results"
          );


        const concierge =
          document.getElementById(
            "rise-skin-concierge"
          );


        if (concierge) {

          concierge.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }

      },
      900
    );
  };


// ============================================================
// EXISTING LIQUID BUTTON BRIDGES
// ============================================================

window.rscSetRoutine =
  function (level) {

    window.RISE_SKIN
      .renderRoutineLevel(
        level
      );
  };


window.rscToggleWhy =
  function () {

    const box =
      document.getElementById(
        "rsc-why-box"
      );


    if (!box) {
      return;
    }


    const isOpen =
      box.style.display !==
      "none";


    box.style.display =
      isOpen
        ? "none"
        : "block";
  };


// ============================================================
// TEMPORARY PLACEHOLDERS
// ============================================================

window.rscCartDemo =
  function () {

    console.log(
      "RISE SKIN: Cart integration comes next."
    );
  };


window.rscIntegrationDemo =
  function () {

    console.log(
      "RISE SKIN: Current-routine integration comes next."
    );
  };


window.rscTreatmentDemo =
  function () {

    const treatment =
      window.RISE_SKIN
        .RESULTS
        ?.data
        ?.treatments
        ?.primary;

    if (!treatment) {
      return;
    }

    const treatmentLinks = {
  "bbl-heroic":
    "https://www.riserejuvenationcenter.com/bbl-heroic/",

  "moxi":
    "https://www.riserejuvenationcenter.com/moxi-laser/",

  "skinpen":
    "https://www.riserejuvenationcenter.com/microneedling/",

  "hydrafacial":
    "https://www.riserejuvenationcenter.com/hydrafacial/",

  "biorepeel":
    "https://www.riserejuvenationcenter.com/biorepeel/",

  "morpheus8":
    "https://www.riserejuvenationcenter.com/rf-microneedling/",

  "neuromodulator":
    "https://www.riserejuvenationcenter.com/dysport/",

  "bio-gel-prf":
    "https://www.riserejuvenationcenter.com/prp-prf/",

  "sculptra":
    "https://www.riserejuvenationcenter.com/sculptra/",

  "facial-balancing":
    "https://www.riserejuvenationcenter.com/facial-balancing/",

  "barrier-review":
    "https://www.riserejuvenationcenter.com/consultation/",

  "acne-consult":
    "https://www.riserejuvenationcenter.com/consultation/"
};
    const destination =
      treatmentLinks[
        treatment.id
      ];

    if (!destination) {
      console.warn(
        "RISE SKIN: No treatment URL mapped for",
        treatment
      );

      return;
    }

    window.open(
      destination,
      "_blank",
      "noopener,noreferrer"
    );
  };


window.rscToggleDebug =
  function () {

    const panel =
      document.getElementById(
        "rsc-debug-panel"
      );


    const output =
      document.getElementById(
        "rsc-debug-output"
      );


    if (
      !panel ||
      !output
    ) {
      return;
    }


    const isOpen =
      panel.style.display !==
      "none";


    panel.style.display =
      isOpen
        ? "none"
        : "block";


    if (
      !isOpen &&
      window.RISE_SKIN.RESULTS.data
    ) {

      output.innerHTML =
        `<pre>${JSON.stringify(
          {
            recommendedLevel:
              window.RISE_SKIN
                .RESULTS
                .data
                .recommendedLevel,

            profile:
              window.RISE_SKIN
                .RESULTS
                .data
                .profile,

            scores:
              window.RISE_SKIN
                .RESULTS
                .data
                .scores,

            needs:
              window.RISE_SKIN
                .RESULTS
                .data
                .needs
          },
          null,
          2
        )}</pre>`;
    }
  };


// ============================================================
// VALIDATION
// ============================================================

window.RISE_SKIN.validateResults =
  function () {

    const required = [
      "buildRoutines",
      "renderRoutineLevel",
      "populateResults"
    ];


    const missing =
      required.filter(
        (name) =>
          typeof window.RISE_SKIN[
            name
          ] !== "function"
      );


    const report = {
      valid:
        missing.length === 0,

      missingFunctions:
        missing
    };


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN CONCIERGE — RESULTS VALIDATION"
      );


      if (report.valid) {

        console.log(
          "Results renderer loaded."
        );

      } else {

        console.error(
          "Missing results dependencies:",
          missing
        );
      }


      console.groupEnd();
    }


    return report;
  };


// ============================================================
// INITIAL CHECK
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Results Renderer Part 1 loaded"
  );

  window.RISE_SKIN
    .validateResults();
}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 2
 * Personalized Narrative Layer
 * ============================================================
 */


// ============================================================
// HELPER: TOP SCORED CONCERNS
// ============================================================

window.RISE_SKIN.getTopScoredConcerns =
  function (result, limit = 3) {

    const scores =
      result?.scores || {};

    const concernKeys = [
      "acne",
      "pores",
      "texture",
      "pigment",
      "redness",
      "fineLines",
      "laxity",
      "dullness",
      "dehydration",
      "underEye"
    ];

    return concernKeys
      .map((key) => ({
        key,
        score: Number(scores[key] || 0)
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  };


// ============================================================
// HELPER: HUMAN CONCERN PHRASES
// ============================================================

window.RISE_SKIN.getConcernPhrase =
  function (key) {

    const map = {
      acne: "breakouts",
      pores: "visible pores",
      texture: "uneven texture",
      pigment: "uneven tone and discoloration",
      redness: "visible redness",
      fineLines: "fine lines and skin aging",
      laxity: "loss of firmness",
      dullness: "dullness and lack of glow",
      dehydration: "dryness and dehydration",
      underEye: "under-eye concerns"
    };

    return map[key] || key;
  };


// ============================================================
// PERSONALIZED SKIN SNAPSHOT
// ============================================================

window.RISE_SKIN.buildPersonalizedSnapshot =
  function (result) {

    const profile =
      result.profile || {};

    const topConcerns =
      window.RISE_SKIN
        .getTopScoredConcerns(
          result,
          3
        );

    const descriptors = [];


    if (profile.skinBehavior) {
      descriptors.push(
        window.RISE_SKIN
          .skinBehaviorLabels[
            profile.skinBehavior
          ] ||
        profile.skinBehavior
      );
    }


    if (
      profile.sensitivity === "resilient"
    ) {
      descriptors.push(
        "relatively resilient"
      );
    }

    if (
      profile.sensitivity === "sensitive"
    ) {
      descriptors.push(
        "somewhat sensitive"
      );
    }

    if (
      profile.sensitivity ===
      "very_sensitive"
    ) {
      descriptors.push(
        "very reactive"
      );
    }


    let opening =
      "Your skin profile";

    if (descriptors.length) {

      opening +=
        " is " +
        descriptors.join(
          " and "
        );
    }


    if (
      profile.barrierStatus === "healthy"
    ) {

      opening +=
        ", with a healthy barrier";

    } else if (
      profile.barrierStatus === "mild"
    ) {

      opening +=
        ", with some signs of barrier stress";

    } else if (
      profile.barrierStatus === "impaired"
    ) {

      opening +=
        ", with a currently compromised barrier";
    }


    opening += ".";


    let context = "";


    if (
      profile.barrierStatus === "healthy" &&
      profile.sensitivity ===
        "resilient"
    ) {

      context =
        " That gives us room to use meaningful correction without making your routine unnecessarily complicated.";

    } else if (
      profile.barrierStatus ===
        "impaired"
    ) {

      context =
        " Right now, strengthening and calming the skin comes before aggressive correction.";

    } else if (
      profile.sensitivity ===
        "very_sensitive" ||
      profile.sensitivity ===
        "sensitive"
    ) {

      context =
        " Your routine should prioritize consistency and tolerability over piling on stronger actives.";
    }


    let opportunity = "";


    if (topConcerns.length) {

      const phrases =
        topConcerns.map(
          (item) =>
            window.RISE_SKIN
              .getConcernPhrase(
                item.key
              )
        );


      if (phrases.length === 1) {

        opportunity =
          ` Your biggest opportunity is improving ${phrases[0]}.`;

      } else if (phrases.length === 2) {

        opportunity =
          ` Your biggest opportunities are improving ${phrases[0]} and ${phrases[1]}.`;

      } else {

        opportunity =
          ` Your biggest opportunities are improving ${phrases[0]}, ${phrases[1]}, and ${phrases[2]}.`;
      }
    }


    return (
      opening +
      context +
      opportunity
    );
  };


// ============================================================
// PERSONALIZED HEADLINE REASON
// ============================================================

window.RISE_SKIN.getHeadlineReasonText =
  function (
    headline,
    result
  ) {

    if (!headline) {
      return "";
    }


    const product =
      headline.product || {};

    const profile =
      result.profile || {};

    const topConcerns =
      window.RISE_SKIN
        .getTopScoredConcerns(
          result,
          3
        );


    const concernText =
      topConcerns
        .map(
          (item) =>
            window.RISE_SKIN
              .getConcernPhrase(
                item.key
              )
        );


    const family =
      headline.family;


    if (
      family === "antioxidant"
    ) {

      return (
        `This gives you broad daytime support for ${
          concernText.length
            ? concernText
                .slice(0, 2)
                .join(" and ")
            : "your biggest skin goals"
        } without adding unnecessary irritation to your routine.`
      );
    }


    if (
      family === "retinoid"
    ) {

      return (
        "This becomes the workhorse of your evening routine—supporting multiple concerns at once while keeping the rest of your plan streamlined."
      );
    }


    if (
      family === "regenerative"
    ) {

      return (
        "This is the premium step that adds the most meaningful extra support beyond your core routine."
      );
    }


    if (
      family === "eye"
    ) {

      return (
        "Your eye area needs a targeted product rather than another general face serum, so this earns its own place in your routine."
      );
    }


    return (
      "This is one of the highest-leverage products for the skin goals you told us matter most."
    );
  };


// ============================================================
// PERSONALIZED WHY TEXT
// ============================================================

window.RISE_SKIN.getPersonalizedProductWhy =
  function (
    item,
    result
  ) {

    const product =
      item.product || {};

    const profile =
      result.profile || {};

    const scores =
      result.scores || {};

    const family =
      item.family;

    const topConcerns =
      window.RISE_SKIN
        .getTopScoredConcerns(
          result,
          3
        );

    const concernPhrases =
      topConcerns.map(
        (entry) =>
          window.RISE_SKIN
            .getConcernPhrase(
              entry.key
            )
      );


    // --------------------------------------------------------
    // ANTIOXIDANT
    // --------------------------------------------------------

    if (
      family === "antioxidant"
    ) {

      let text =
        `${product.name} gives you broad antioxidant support in the morning`;


      if (
        scores.redness > 0 ||
        scores.pigment > 0
      ) {

        text +=
          ", which is especially useful because your profile shows";


        const specific = [];

        if (scores.redness > 0) {
          specific.push(
            "visible redness"
          );
        }

        if (scores.pigment > 0) {
          specific.push(
            "uneven tone"
          );
        }

        text +=
          " " +
          specific.join(" and ");
      }


      const routine =
        result.routines[
          result.recommendedLevel ||
          "complete"
        ];


      const hasRetinoid =
        routine &&
        routine.pm.some(
          (entry) =>
            entry.family ===
            "retinoid"
        );


      if (hasRetinoid) {

        text +=
          ". It also gives you meaningful daytime support without adding another aggressive corrective alongside your evening retinoid";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // RETINOID
    // --------------------------------------------------------

    if (
      family === "retinoid"
    ) {

      let text =
        `${product.name} is your evening workhorse because several of your goals benefit from consistent retinoid support`;


      const targets = [];

      if (scores.acne > 0) {
        targets.push("breakouts");
      }

      if (scores.pores > 0) {
        targets.push("pores");
      }

      if (scores.texture > 0) {
        targets.push("texture");
      }

      if (scores.pigment > 0) {
        targets.push("tone");
      }

      if (scores.fineLines > 0) {
        targets.push("fine lines");
      }


      if (targets.length) {

        text +=
          "—including " +
          targets
            .slice(0, 4)
            .join(", ");
      }


      if (
        profile.sensitivity ===
          "resilient" &&
        profile.barrierStatus ===
          "healthy"
      ) {

        text +=
          ". Your skin also appears resilient with a healthy barrier, so this is an appropriate place to concentrate your corrective power";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // REGENERATIVE
    // --------------------------------------------------------

    if (
      family === "regenerative"
    ) {

      return (
        `${product.name} is included in your Elevated plan because it adds regenerative support beyond the essentials without duplicating the job of your core corrective products.`
      );
    }


    // --------------------------------------------------------
    // EYE
    // --------------------------------------------------------

    if (
      family === "eye"
    ) {

      const eye =
        window.RISE_SKIN
          .asArray(
            profile.eyeConcerns
          );


      const concerns = [];


      if (
        eye.includes(
          "fine_lines"
        )
      ) {
        concerns.push(
          "fine lines"
        );
      }

      if (
        eye.includes(
          "puffiness"
        )
      ) {
        concerns.push(
          "puffiness"
        );
      }

      if (
        eye.includes(
          "dark_circles"
        )
      ) {
        concerns.push(
          "dark circles"
        );
      }

      if (
        eye.includes(
          "hollowing"
        )
      ) {
        concerns.push(
          "hollowing"
        );
      }


      return (
        `${product.name} is targeted specifically to the eye area${
          concerns.length
            ? ` because you told us ${concerns.join(", ")} are part of the picture`
            : ""
        }.`
      );
    }


    // --------------------------------------------------------
    // CLEANSER
    // --------------------------------------------------------

    if (
      family === "cleanser"
    ) {

      return (
        `${product.name} gives the routine a dependable cleansing foundation without competing with the corrective products doing the heavier lifting.`
      );
    }


    // --------------------------------------------------------
    // SPF
    // --------------------------------------------------------

    if (
      family === "spf"
    ) {

      let text =
        `${product.name} protects the progress you're making with the rest of your routine`;


      if (
        profile.sunExposure ===
          "high" ||
        profile.sunExposure ===
          "very_high"
      ) {

        text +=
          ", which matters even more because you reported significant regular sun exposure";
      }


      if (
        profile.spfPreference &&
        profile.spfPreference.length
      ) {

        text +=
          ". It also scored well for the sunscreen preferences you selected";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // MOISTURIZER
    // --------------------------------------------------------

    if (
      family === "moisturizer"
    ) {

      return (
        `${product.name} is here to support hydration and barrier function so your corrective steps remain comfortable and sustainable.`
      );
    }


    // --------------------------------------------------------
    // SUPPORT
    // --------------------------------------------------------

    if (
      family === "support"
    ) {

      return (
        `${product.name} earned a place because your profile shows additional benefit from calming, recovery, or barrier support.`
      );
    }


    // --------------------------------------------------------
    // FALLBACK
    // --------------------------------------------------------

    if (
      concernPhrases.length
    ) {

      return (
        `${product.name} scored strongly for ${concernPhrases.slice(0, 3).join(", ")} based on your quiz answers.`
      );
    }


    return (
      `${product.name} scored highly for your overall skin profile and routine goals.`
    );
  };


// ============================================================
// OVERRIDE PRODUCT WHY
// ============================================================

window.RISE_SKIN.getProductWhyTextBase =
  window.RISE_SKIN.getProductWhyText;


window.RISE_SKIN.getProductWhyText =
  function (item) {

    if (
      window.RISE_SKIN.RESULTS &&
      window.RISE_SKIN.RESULTS.data
    ) {

      return window.RISE_SKIN
        .getPersonalizedProductWhy(
          item,
          window.RISE_SKIN
            .RESULTS.data
        );
    }


    return window.RISE_SKIN
      .getProductWhyTextBase(
        item
      );
  };


// ============================================================
// OVERRIDE RESULTS POPULATION
// ============================================================

window.RISE_SKIN.populateResultsBase =
  window.RISE_SKIN.populateResults;


window.RISE_SKIN.populateResults =
  function (result) {

    // Run existing population first.
    window.RISE_SKIN
      .populateResultsBase(
        result
      );


    // --------------------------------------------------------
    // SMARTER SNAPSHOT
    // --------------------------------------------------------

    const snapshot =
      document.getElementById(
        "rsc-snapshot-text"
      );


    if (snapshot) {

      snapshot.textContent =
        window.RISE_SKIN
          .buildPersonalizedSnapshot(
            result
          );
    }


    // --------------------------------------------------------
    // SMARTER HEADLINE COPY
    // --------------------------------------------------------

    const headline =
      window.RISE_SKIN
        .getHeadlineProduct(
          result
        );


    const reason =
      document.getElementById(
        "rsc-headline-reason"
      );


    const whyBox =
      document.getElementById(
        "rsc-why-box"
      );


    if (headline) {

      if (reason) {

        reason.textContent =
          window.RISE_SKIN
            .getHeadlineReasonText(
              headline,
              result
            );
      }


      if (whyBox) {

        whyBox.textContent =
          window.RISE_SKIN
            .getPersonalizedProductWhy(
              headline,
              result
            );
      }
    }


    // Re-render current routine so every
    // product card gets personalized why copy.
    window.RISE_SKIN
      .renderRoutineLevel(
        window.RISE_SKIN
          .RESULTS
          .activeLevel ||
        result.recommendedLevel ||
        "complete"
      );
  };


// ============================================================
// DEBUG
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Personalized Results Layer loaded"
  );

}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 3
 * Product Selection Intelligence
 * ============================================================
 */


// ============================================================
// GET FAMILY CANDIDATES FROM FINAL RANKING
// ============================================================

window.RISE_SKIN.getRankedFamilyCandidates =
  function (family, result) {

    if (
      !result ||
      !result.ranking ||
      !Array.isArray(result.ranking.eligible)
    ) {
      return [];
    }

    return result.ranking.eligible
      .filter((item) => {

        if (
          window.RISE_SKIN
            .requiresProviderReview(
              item.product
            )
        ) {
          return false;
        }

        return (
          window.RISE_SKIN
            .getProductFamily(
              item.product
            ) === family
        );
      })
      .sort(
        (a, b) =>
          b.score - a.score
      );
  };


// ============================================================
// SHOULD THIS PRODUCT HAVE "WHY THIS PRODUCT?"
// ============================================================

window.RISE_SKIN.shouldShowProductWhy =
  function (item, result) {

    const family =
      item.family;

    const profile =
      result?.profile || {};


    // --------------------------------------------------------
    // CLEANSER
    // Foundational. No need to make client open extra copy.
    // --------------------------------------------------------

    if (family === "cleanser") {
      return false;
    }


    // --------------------------------------------------------
    // SPF
    // Show why only when the particular SPF was meaningfully
    // personalized by lifestyle or preference.
    // --------------------------------------------------------

    if (family === "spf") {

      const preferences =
        window.RISE_SKIN.asArray(
          profile.spfPreference
        );


      const meaningfulPreference =
        preferences.some(
          (preference) =>
            [
              "tinted",
              "sheer",
              "glowy",
              "matte",
              "reapplication"
            ].includes(
              preference
            )
        );


      const highSun =
        profile.sunExposure === "high" ||
        profile.sunExposure ===
          "very_high";


      return (
        meaningfulPreference ||
        highSun
      );
    }


    // Everything corrective / targeted / premium
    // should explain why it earned its spot.
    return [
      "antioxidant",
      "corrective",
      "retinoid",
      "exfoliant",
      "acne_corrective",
      "pigment_corrective",
      "regenerative",
      "eye",
      "support",
      "moisturizer"
    ].includes(family);
  };


// ============================================================
// SELECTION CONTEXT
// Why did THIS product beat the alternatives?
// ============================================================

window.RISE_SKIN.getSelectionContext =
  function (item, result) {

    const family =
      item.family;


    const candidates =
      window.RISE_SKIN
        .getRankedFamilyCandidates(
          family,
          result
        );


    if (!candidates.length) {
      return null;
    }


    const winner =
      candidates[0];


    const currentId =
      window.RISE_SKIN
        .getProductId(
          item.product
        );


    const winnerId =
      window.RISE_SKIN
        .getProductId(
          winner.product
        );


    if (
      currentId !== winnerId
    ) {
      return null;
    }


    const runnerUp =
      candidates[1] || null;


    return {
      isFamilyWinner: true,
      candidateCount:
        candidates.length,

      winnerScore:
        winner.score,

      runnerUpScore:
        runnerUp
          ? runnerUp.score
          : null,

      runnerUp:
        runnerUp
          ? runnerUp.product
          : null
    };
  };


// ============================================================
// SPF PERSONALIZATION TEXT
// ============================================================

window.RISE_SKIN.getSPFPreferenceReason =
  function (
    product,
    profile
  ) {

    const preferences =
      window.RISE_SKIN.asArray(
        profile.spfPreference
      );


    const tags =
      window.RISE_SKIN.asArray(
        product.reasonTags
      );


    const reasons = [];


    if (
      preferences.includes("tinted") &&
      tags.includes("TINTED")
    ) {
      reasons.push(
        "you told us you prefer a tinted SPF"
      );
    }


    if (
      preferences.includes("sheer") &&
      (
        tags.includes("SHEER") ||
        tags.includes("INVISIBLE")
      )
    ) {
      reasons.push(
        "you prefer sunscreen to feel sheer or nearly invisible"
      );
    }


    if (
      preferences.includes("glowy") &&
      (
        tags.includes("GLOW") ||
        tags.includes("RADIANCE") ||
        tags.includes("DEWY_FINISH")
      )
    ) {
      reasons.push(
        "you prefer a more radiant finish"
      );
    }


    if (
      preferences.includes("matte") &&
      tags.includes("MATTE")
    ) {
      reasons.push(
        "you prefer a matte or natural finish"
      );
    }


    if (
      preferences.includes(
        "reapplication"
      ) &&
      tags.includes(
        "REAPPLICATION"
      )
    ) {
      reasons.push(
        "easy reapplication matters to you"
      );
    }


    return reasons;
  };


// ============================================================
// MORE INTELLIGENT PRODUCT WHY
// ============================================================

window.RISE_SKIN.getPersonalizedProductWhy =
  function (
    item,
    result
  ) {

    const product =
      item.product || {};

    const profile =
      result.profile || {};

    const scores =
      result.scores || {};

    const family =
      item.family;


    const selection =
      window.RISE_SKIN
        .getSelectionContext(
          item,
          result
        );


    // --------------------------------------------------------
    // ANTIOXIDANT
    // --------------------------------------------------------

    if (
      family === "antioxidant"
    ) {

      const targets = [];

      if (scores.redness > 0) {
        targets.push(
          "visible redness"
        );
      }

      if (scores.pigment > 0) {
        targets.push(
          "uneven tone"
        );
      }

      if (scores.dullness > 0) {
        targets.push(
          "dullness"
        );
      }

      if (scores.fineLines > 0) {
        targets.push(
          "long-term skin aging"
        );
      }


      let text =
        `${product.name} rose to the top of your antioxidant options`;


      if (targets.length) {

        text +=
          ` because it supports several things showing up in your profile at once—especially ${targets
            .slice(0, 3)
            .join(", ")}`;
      }


      const activeRoutine =
        result.routines[
          result.recommendedLevel ||
          "complete"
        ];


      const hasRetinoid =
        activeRoutine &&
        activeRoutine.pm.some(
          (entry) =>
            entry.family ===
            "retinoid"
        );


      if (hasRetinoid) {

        text +=
          ". Your evening retinoid is already handling stronger correction, so this gives us broad daytime support without unnecessarily stacking another aggressive active";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // RETINOID
    // --------------------------------------------------------

    if (
      family === "retinoid"
    ) {

      const targets = [];

      if (scores.acne > 0) {
        targets.push("breakouts");
      }

      if (scores.pores > 0) {
        targets.push("pores");
      }

      if (scores.texture > 0) {
        targets.push("texture");
      }

      if (scores.pigment > 0) {
        targets.push("uneven tone");
      }

      if (scores.fineLines > 0) {
        targets.push("fine lines");
      }


      let text =
        `${product.name} was the strongest retinoid fit for your profile`;


      if (targets.length) {

        text +=
          ` because one evening step can support ${targets
            .slice(0, 4)
            .join(", ")}`;
      }


      if (
        profile.sensitivity ===
          "resilient" &&
        profile.barrierStatus ===
          "healthy"
      ) {

        text +=
          ". You also reported resilient skin and a healthy barrier, which gives us more room for meaningful correction";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // REGENERATIVE
    // --------------------------------------------------------

    if (
      family === "regenerative"
    ) {

      let text =
        `${product.name} earned the premium regenerative spot in your Elevated routine`;


      if (scores.fineLines > 0) {

        text +=
          " because your profile shows an opportunity for additional aging support";
      }


      if (scores.redness > 0) {

        text +=
          scores.fineLines > 0
            ? " along with visible redness"
            : " because visible redness is also part of your skin picture";
      }


      text +=
        ". It adds something distinct beyond your cleanser, antioxidant, SPF, and evening corrective rather than simply adding another redundant serum";


      return text + ".";
    }


    // --------------------------------------------------------
    // EYE
    // --------------------------------------------------------

    if (
      family === "eye"
    ) {

      const eye =
        window.RISE_SKIN.asArray(
          profile.eyeConcerns
        );


      const labels = {
        fine_lines:
          "fine lines or crepey texture",

        puffiness:
          "puffiness",

        dark_circles:
          "dark circles",

        hollowing:
          "hollowing",

        prevention:
          "prevention"
      };


      const selected =
        eye
          .filter(
            (value) =>
              labels[value]
          )
          .map(
            (value) =>
              labels[value]
          );


      let text =
        `${product.name} was the strongest targeted eye-area match`;


      if (selected.length) {

        text +=
          ` because you specifically identified ${selected.join(
            ", "
          )}`;
      }


      if (
        eye.includes("hollowing")
      ) {

        text +=
          ". Skincare can support the quality of the eye-area skin, but true hollowing is structural, so we'll address that separately in your treatment recommendations";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // SPF
    // --------------------------------------------------------

    if (
      family === "spf"
    ) {

      const preferenceReasons =
        window.RISE_SKIN
          .getSPFPreferenceReason(
            product,
            profile
          );


      let text =
        `${product.name} was selected as your daily SPF`;


      if (
        preferenceReasons.length
      ) {

        text +=
          ` because ${preferenceReasons.join(
            " and "
          )}`;
      }


      if (
        profile.sunExposure ===
          "high" ||
        profile.sunExposure ===
          "very_high"
      ) {

        text +=
          preferenceReasons.length
            ? ", and"
            : " because";

        text +=
          " your regular sun exposure makes consistent protection especially important";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // MOISTURIZER
    // --------------------------------------------------------

    if (
      family === "moisturizer"
    ) {

      let text =
        `${product.name} earned a place because your skin profile shows a meaningful need for hydration or barrier support`;


      if (
        profile.barrierStatus ===
          "impaired"
      ) {

        text +=
          ". Your barrier is currently compromised, so comfort and recovery need to come before adding more aggressive correction";
      }


      return text + ".";
    }


    // --------------------------------------------------------
    // SUPPORT
    // --------------------------------------------------------

    if (
      family === "support"
    ) {

      return (
        `${product.name} was selected because it adds calming or recovery support that is not already being fully handled by the stronger corrective steps in your routine.`
      );
    }


    // --------------------------------------------------------
    // PIGMENT CORRECTIVE
    // --------------------------------------------------------

    if (
      family ===
        "pigment_corrective"
    ) {

      return (
        `${product.name} scored highest among the appropriate pigment-focused options for your profile because discoloration is a meaningful part of your skin goals while still fitting your sensitivity and barrier status.`
      );
    }


    // --------------------------------------------------------
    // ACNE CORRECTIVE
    // --------------------------------------------------------

    if (
      family ===
        "acne_corrective"
    ) {

      return (
        `${product.name} rose to the top of the appropriate acne-focused options based on the type and level of breakout activity you reported.`
      );
    }


    // --------------------------------------------------------
    // GENERAL CORRECTIVE
    // --------------------------------------------------------

    if (
      family === "corrective"
    ) {

      return (
        `${product.name} scored strongly across several of your highest-priority concerns, which lets us address more than one goal without unnecessarily adding extra products.`
      );
    }


    // --------------------------------------------------------
    // FALLBACK
    // --------------------------------------------------------

    if (
      selection &&
      selection.isFamilyWinner
    ) {

      return (
        `${product.name} was the highest-scoring appropriate option in its category for your quiz profile.`
      );
    }


    return (
      `${product.name} was selected because it fits your skin profile and contributes something distinct to your routine.`
    );
  };


// ============================================================


// ============================================================
// PRODUCT USAGE INSTRUCTIONS
// ============================================================

window.RISE_SKIN.renderProductUsage =
  function (product) {
    const usage = product?.usage;

    if (!usage || !usage.directions) {
      return "";
    }

    const timing = usage.timing || "";

    return `
      <div
        class="rsc-product-usage"
        style="
          margin-top:12px;
          padding:12px 14px;
          border-radius:10px;
          background:rgba(0,0,0,0.035);
          font-size:0.92em;
          line-height:1.45;
        "
      >
        <div
          style="
            font-size:0.76em;
            font-weight:700;
            letter-spacing:0.08em;
            margin-bottom:4px;
          "
        >
          HOW TO USE
        </div>
        ${
          timing
            ? `<strong>${timing}</strong> · `
            : ""
        }${usage.directions}
      </div>
    `;
  };

// PRODUCT CARD — REFINED
// ============================================================

window.RISE_SKIN.renderRoutineProduct =
  function (
    item,
    index
  ) {

    const product =
      item.product || {};


    const result =
      window.RISE_SKIN
        .RESULTS
        .data;


    const showWhy =
      window.RISE_SKIN
        .shouldShowProductWhy(
          item,
          result
        );


    const whyText =
      showWhy
        ? window.RISE_SKIN
            .getPersonalizedProductWhy(
              item,
              result
            )
        : "";


    return `
      <div
        class="rsc-product-card"
        data-rise-product-id="${
          product.id || ""
        }"
      >

        <div class="rsc-product-step">
          ${String(index + 1).padStart(
            2,
            "0"
          )}
        </div>


        <div class="rsc-product-main">

          <div class="rsc-product-brand">
            ${product.brand || ""}
          </div>


          <h3 class="rsc-product-name">
            ${product.name || ""}
          </h3>


          ${
            item.reason
              ? `
                <p class="rsc-product-reason">
                  ${item.reason}
                </p>
              `
              : ""
          }


          ${
            window.RISE_SKIN
              .renderProductUsage(product)
          }


          ${
            showWhy
              ? `
                <button
                  type="button"
                  class="
                    rsc-text-link
                    rise-product-more
                  "
                  data-rise-more-product="${
                    product.id || ""
                  }"
                >
                  WHY THIS PRODUCT? →
                </button>

                <div
                  class="
                    rsc-more-info
                    rise-product-more-box
                  "
                  data-rise-more-box="${
                    product.id || ""
                  }"
                  style="display:none;"
                >
                  ${whyText}
                </div>
              `
              : ""
          }

        </div>

      </div>
    `;
  };


// ============================================================
// REFRESH RESULTS AFTER PART 3
// ============================================================

if (
  window.RISE_SKIN.RESULTS &&
  window.RISE_SKIN.RESULTS.data
) {

  window.RISE_SKIN
    .renderRoutineLevel(
      window.RISE_SKIN
        .RESULTS
        .activeLevel ||
      "complete"
    );
}


if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Product Selection Intelligence loaded"
  );

}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 4
 * Commerce + Product Display Layer
 * ============================================================
 */


// ============================================================
// PRODUCT COMMERCE STATE
// ============================================================

window.RISE_SKIN.getProductCommerceState =
  function (product) {

    const commerce =
      product?.commerce || {};

    const route =
      commerce.route || null;

    const hasVariant =
      Boolean(commerce.variantId);

    const hasHandle =
      Boolean(commerce.productHandle);


    // Shopify product fully mapped.
    if (
      route === "shopify" &&
      hasVariant
    ) {
      return {
        type: "add",
        label: "ADD TO ROUTINE",
        enabled: true
      };
    }


    // Shopify product has page but not variant mapping.
    if (
      route === "shopify" &&
      hasHandle
    ) {
      return {
        type: "view",
        label: "VIEW PRODUCT",
        enabled: true
      };
    }


    // SkinBetter routing.
    if (
      route === "skinbetter" &&
      hasHandle
    ) {
      return {
        type: "view",
        label: "VIEW PRODUCT",
        enabled: true
      };
    }


    // Not mapped yet.
    return {
      type: "available",
      label: "AVAILABLE AT RISE",
      enabled: false
    };
  };


// ============================================================
// PRODUCT URL
// ============================================================

window.RISE_SKIN.getProductURL =
  function (product) {

    const commerce =
      product?.commerce || {};


    if (!commerce.productHandle) {
      return null;
    }


    /*
     * Standard Shopify route.
     * SkinBetter routing can be customized later
     * if its purchase flow differs.
     */
    return (
      "/products/" +
      commerce.productHandle
    );
  };


// ============================================================
// PRODUCT IMAGE
// ============================================================

window.RISE_SKIN.getProductImage =
  function (product) {

    /*
     * Eventually we can populate imageUrl directly
     * from Shopify or product metadata.
     */

    if (product.imageUrl) {
      return product.imageUrl;
    }


    return null;
  };


// ============================================================
// PRODUCT PRICE
// ============================================================

window.RISE_SKIN.getProductPrice =
  function (product) {

    if (
      product.price !== undefined &&
      product.price !== null
    ) {

      const numeric =
        Number(product.price);


      if (!Number.isNaN(numeric)) {

        return new Intl.NumberFormat(
          "en-US",
          {
            style: "currency",
            currency: "USD"
          }
        ).format(numeric);
      }
    }


    return null;
  };


// ============================================================
// PRODUCT CTA
// ============================================================

window.RISE_SKIN.renderProductCTA =
  function (product) {

    const state =
      window.RISE_SKIN
        .getProductCommerceState(
          product
        );


    if (
      state.type === "add"
    ) {

      return `
        <button
          type="button"
          class="rsc-product-cta"
          data-rise-add-variant="${
            product.commerce.variantId
          }"
          data-rise-product-id="${
            product.id || ""
          }"
        >
          ${state.label}
        </button>
      `;
    }


    if (
      state.type === "view"
    ) {

      const url =
        window.RISE_SKIN
          .getProductURL(
            product
          );


      if (url) {

        return `
          <a
            class="rsc-product-cta"
            href="${url}"
          >
            ${state.label}
          </a>
        `;
      }
    }


    return `
      <div
        class="rsc-product-availability"
      >
        ${state.label}
      </div>
    `;
  };


// ============================================================
// REPLACE PRODUCT CARD RENDERER
// ============================================================

window.RISE_SKIN.renderRoutineProduct =
  function (
    item,
    index
  ) {

    const product =
      item.product || {};


    const result =
      window.RISE_SKIN
        .RESULTS
        .data;


    const showWhy =
      window.RISE_SKIN
        .shouldShowProductWhy(
          item,
          result
        );


    const whyText =
      showWhy
        ? window.RISE_SKIN
            .getPersonalizedProductWhy(
              item,
              result
            )
        : "";


    const image =
      window.RISE_SKIN
        .getProductImage(
          product
        );


    const price =
      window.RISE_SKIN
        .getProductPrice(
          product
        );


    return `
      <div
        class="rsc-product-card"
        data-rise-product-id="${
          product.id || ""
        }"
      >

        ${
          image
            ? `
              <div
                class="rsc-product-image-wrap"
              >
                <img
                  src="${image}"
                  alt="${product.name || ""}"
                  class="rsc-product-image"
                  loading="lazy"
                >
              </div>
            `
            : ""
        }


        <div class="rsc-product-step">
          ${String(index + 1).padStart(
            2,
            "0"
          )}
        </div>


        <div class="rsc-product-main">

          <div class="rsc-product-brand">
            ${product.brand || ""}
          </div>


          <h3 class="rsc-product-name">
            ${product.name || ""}
          </h3>


          ${
            price
              ? `
                <div
                  class="rsc-product-price"
                >
                  ${price}
                </div>
              `
              : ""
          }


          ${
            item.reason
              ? `
                <p class="rsc-product-reason">
                  ${item.reason}
                </p>
              `
              : ""
          }


          ${
            window.RISE_SKIN
              .renderProductUsage(product)
          }


          ${
            showWhy
              ? `
                <button
                  type="button"
                  class="
                    rsc-text-link
                    rise-product-more
                  "
                  data-rise-more-product="${
                    product.id || ""
                  }"
                >
                  WHY THIS PRODUCT? →
                </button>

                <div
                  class="
                    rsc-more-info
                    rise-product-more-box
                  "
                  data-rise-more-box="${
                    product.id || ""
                  }"
                  style="display:none;"
                >
                  ${whyText}
                </div>
              `
              : ""
          }


          <div
            class="rsc-product-commerce"
          >
            ${
              window.RISE_SKIN
                .renderProductCTA(
                  product
                )
            }
          </div>

        </div>

      </div>
    `;
  };


// ============================================================
// SINGLE PRODUCT ADD-TO-CART
// ============================================================

window.RISE_SKIN.addVariantToCart =
  async function (
    variantId,
    quantity = 1
  ) {

    if (!variantId) {
      return false;
    }


    try {

      const response =
        await fetch(
          "/cart/add.js",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              "Accept":
                "application/json"
            },

            body:
              JSON.stringify({
                items: [
                  {
                    id:
                      Number(
                        variantId
                      ),
                    quantity
                  }
                ]
              })
          }
        );


      if (!response.ok) {

        throw new Error(
          "Unable to add product to cart."
        );
      }


      return true;

    } catch (error) {

      console.error(
        "RISE SKIN cart error:",
        error
      );

      return false;
    }
  };


// ============================================================
// BIND PRODUCT CART BUTTONS
// ============================================================

window.RISE_SKIN.bindCommerceButtons =
  function () {

    document
      .querySelectorAll(
        "[data-rise-add-variant]"
      )
      .forEach(
        (button) => {

          button.onclick =
            async function () {

              const variantId =
                button.dataset
                  .riseAddVariant;


              const original =
                button.textContent;


              button.disabled =
                true;

              button.textContent =
                "ADDING…";


              const success =
                await window.RISE_SKIN
                  .addVariantToCart(
                    variantId
                  );


              if (success) {

                button.textContent =
                  "ADDED ✓";

              } else {

                button.textContent =
                  "TRY AGAIN";

                button.disabled =
                  false;
              }


              if (success) {

                window.setTimeout(
                  function () {

                    button.textContent =
                      original;

                    button.disabled =
                      false;
                  },
                  1800
                );
              }
            };
        }
      );
  };


// ============================================================
// WRAP EXISTING PRODUCT BUTTON BINDER
// ============================================================

window.RISE_SKIN.bindProductMoreButtonsBase =
  window.RISE_SKIN
    .bindProductMoreButtons;


window.RISE_SKIN.bindProductMoreButtons =
  function () {

    window.RISE_SKIN
      .bindProductMoreButtonsBase();


    window.RISE_SKIN
      .bindCommerceButtons();
  };


// ============================================================
// REFRESH CURRENT ROUTINE
// ============================================================

if (
  window.RISE_SKIN.RESULTS &&
  window.RISE_SKIN.RESULTS.data
) {

  window.RISE_SKIN
    .renderRoutineLevel(
      window.RISE_SKIN
        .RESULTS
        .activeLevel ||
      "complete"
    );
}


if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Commerce Display Layer loaded"
  );

}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 5
 * Live Shopify Product Data
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// LIVE SHOPIFY PRODUCT CACHE
// ============================================================

window.RISE_SKIN.SHOPIFY_PRODUCTS =
  window.RISE_SKIN.SHOPIFY_PRODUCTS || {};

window.RISE_SKIN.SHOPIFY_DATA_READY = false;


// ============================================================
// LOAD LIVE SHOPIFY CATALOG
// ============================================================

window.RISE_SKIN.loadLiveShopifyProducts =
  async function () {

    try {

      const response =
        await fetch(
          "/products.json?limit=250",
          {
            credentials: "same-origin"
          }
        );


      if (!response.ok) {

        throw new Error(
          `Unable to load Shopify products: ${response.status}`
        );
      }


      const data =
        await response.json();


      const shopifyProducts =
        data.products || [];


      const byHandle = {};


      shopifyProducts.forEach(
        (shopifyProduct) => {

          if (
            shopifyProduct &&
            shopifyProduct.handle
          ) {

            byHandle[
              shopifyProduct.handle
            ] = shopifyProduct;
          }
        }
      );


      window.RISE_SKIN
        .SHOPIFY_PRODUCTS =
        byHandle;


      // --------------------------------------------------------
      // ENRICH CONCIERGE PRODUCTS
      // --------------------------------------------------------

      const conciergeProducts =
        window.RISE_SKIN_PRODUCTS || [];


      conciergeProducts.forEach(
        (product) => {

          const handle =
            product?.commerce
              ?.productHandle;


          if (!handle) {
            return;
          }


          const shopifyProduct =
            byHandle[handle];


          if (!shopifyProduct) {
            return;
          }


         const variants =
  shopifyProduct.variants || [];

const mappedVariantId =
  Number(
    product?.commerce
      ?.variantId || 0
  );

const requiresVariantSelection =
  Boolean(
    product?.commerce
      ?.requiresVariantSelection
  );

const variant =
  requiresVariantSelection
    ? null
    : (
        variants.find(
          (candidate) =>
            Number(candidate.id) ===
            mappedVariantId
        ) ||
        variants[0] ||
        null
      );

const displayVariant =
  variant ||
  variants.find(
    (candidate) =>
      Boolean(candidate.available)
  ) ||
  variants[0] ||
  null;

const anyVariantAvailable =
  variants.some(
    (candidate) =>
      Boolean(candidate.available)
  );


          // Live product data.
          product.shopifyTitle =
            shopifyProduct.title || null;


          product.imageUrl =
            shopifyProduct.images?.[0]?.src ||
            shopifyProduct.featured_image ||
            null;


          product.price =
  displayVariant?.price !== undefined
    ? Number(displayVariant.price)
    : null;


          product.available =
  requiresVariantSelection
    ? anyVariantAvailable
    : (
        variant
          ? Boolean(variant.available)
          : Boolean(
              shopifyProduct.available
            )
      );


          product.shopifyData = {
            title:
              shopifyProduct.title ||
              product.name,

            handle:
              shopifyProduct.handle,

            productId:
              shopifyProduct.id,

            variantId:
              variant?.id || null,

            price:
  displayVariant?.price || null,

available:
  requiresVariantSelection
    ? anyVariantAvailable
    : (
        variant
          ? Boolean(
              variant.available
            )
          : Boolean(
              shopifyProduct.available
            )
      ),

            image:
              product.imageUrl
          };
        }
      );


      window.RISE_SKIN
        .SHOPIFY_DATA_READY =
        true;


      if (window.RISE_DEBUG) {

        console.group(
          "RISE SKIN — LIVE SHOPIFY DATA"
        );

        console.log(
          "Shopify products loaded:",
          shopifyProducts.length
        );

        console.log(
          "Concierge products enriched:",
          conciergeProducts.filter(
            product =>
              Boolean(
                product.shopifyData
              )
          ).length
        );

        console.groupEnd();
      }


      window.dispatchEvent(
        new CustomEvent(
          "rise:shopify-data-ready"
        )
      );


      return true;


    } catch (error) {

      console.error(
        "RISE SKIN — live Shopify product loading failed:",
        error
      );

      return false;
    }
  };


// ============================================================
// LIVE PRODUCT IMAGE
// ============================================================

window.RISE_SKIN.getProductImage =
  function (product) {

    return (
      product?.shopifyData?.image ||
      product?.imageUrl ||
      null
    );
  };


// ============================================================
// LIVE PRODUCT PRICE
// ============================================================

window.RISE_SKIN.getProductPrice =
  function (product) {

    const price =
      product?.shopifyData?.price ??
      product?.price;


    if (
      price === undefined ||
      price === null ||
      price === ""
    ) {

      return null;
    }


    const numeric =
      Number(price);


    if (Number.isNaN(numeric)) {
      return null;
    }


    return new Intl.NumberFormat(
      "en-US",
      {
        style: "currency",
        currency: "USD"
      }
    ).format(numeric);
  };


// ============================================================
// PRODUCT AVAILABILITY
// ============================================================

window.RISE_SKIN.isProductAvailable =
  function (product) {

    if (
      product?.shopifyData &&
      product.shopifyData.available ===
        false
    ) {

      return false;
    }


    return true;
  };


// ============================================================
// UPDATED PRODUCT CTA
// ============================================================

window.RISE_SKIN.renderProductCTA =
  function (product) {

    const commerce =
      product?.commerce || {};


    const available =
      window.RISE_SKIN
        .isProductAvailable(
          product
        );

if (
  commerce.route === "shopify" &&
  commerce.requiresVariantSelection &&
  commerce.productHandle
) {

  return `
    <a
      class="rsc-product-cta"
      href="/products/${
        commerce.productHandle
      }"
      target="_blank"
      rel="noopener noreferrer"
    >
      CHOOSE SHADE →
    </a>
  `;
}

    if (!available) {

      return `
        <div
          class="rsc-product-availability"
        >
          CURRENTLY UNAVAILABLE
        </div>
      `;
    }


    if (
      commerce.route === "shopify" &&
      commerce.variantId
    ) {

      return `
        <button
          type="button"
          class="rsc-product-cta"
          data-rise-add-variant="${
            commerce.variantId
          }"
          data-rise-product-id="${
            product.id || ""
          }"
        >
          ADD TO MY ROUTINE →
        </button>
      `;
    }


    if (commerce.productHandle) {

      return `
        <a
          class="rsc-product-cta"
          href="/products/${
            commerce.productHandle
          }"
          target="_blank"
          rel="noopener noreferrer"
        >
          VIEW PRODUCT
        </a>
      `;
    }


    return `
      <div
        class="rsc-product-availability"
      >
        AVAILABLE AT RISE
      </div>
    `;
  };


// ============================================================
// REFRESH RESULTS AFTER SHOPIFY DATA LOADS
// ============================================================

window.addEventListener(
  "rise:shopify-data-ready",
  function () {

    if (
      window.RISE_SKIN.RESULTS &&
      window.RISE_SKIN.RESULTS.data
    ) {

      window.RISE_SKIN
        .renderRoutineLevel(
          window.RISE_SKIN
            .RESULTS
            .activeLevel ||
          "complete"
        );
    }
  }
);


// ============================================================
// INITIALIZE
// ============================================================

async function initializeRiseShopifyData() {

  // Commerce map may still be loading.
  if (
    !window.RISE_SKIN_COMMERCE_READY
  ) {

    await new Promise(
      (resolve) => {

        const timeout =
          window.setTimeout(
            resolve,
            3000
          );


        window.addEventListener(
          "rise:commerce-ready",
          function ready() {

            window.clearTimeout(
              timeout
            );

            resolve();

          },
          {
            once: true
          }
        );
      }
    );
  }


  await window.RISE_SKIN
    .loadLiveShopifyProducts();
}


initializeRiseShopifyData();


if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Live Shopify Product Layer loaded"
  );
}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 6
 * Complete Routine Cart + Subscription-Ready Commerce
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// GET ACTIVE ROUTINE
// ============================================================

window.RISE_SKIN.getActiveRoutine =
  function () {

    const result =
      window.RISE_SKIN.RESULTS?.data;

    if (!result?.routines) {
      return null;
    }

    const level =
      window.RISE_SKIN.RESULTS
        ?.activeLevel ||
      result.recommendedLevel ||
      "complete";

    return (
      result.routines[level] ||
      result.routines.complete ||
      null
    );
  };


// ============================================================
// GET UNIQUE PRODUCTS IN ACTIVE ROUTINE
//
// Prevents duplicate AM + PM products from being added twice.
// ============================================================

window.RISE_SKIN.getActiveRoutineProducts =
  function () {

    const routine =
      window.RISE_SKIN
        .getActiveRoutine();

    if (!routine) {
      return [];
    }

    const entries = [
      ...(routine.am || []),
      ...(routine.pm || [])
    ];

    const unique = new Map();

    entries.forEach(
      (item) => {

        const product =
          item?.product;

        if (
          !product ||
          !product.id
        ) {
          return;
        }

        if (
          !unique.has(product.id)
        ) {
          unique.set(
            product.id,
            product
          );
        }
      }
    );

    return Array.from(
      unique.values()
    );
  };


// ============================================================
// GET CART-ELIGIBLE ROUTINE PRODUCTS
// ============================================================

window.RISE_SKIN.getActiveRoutineCartProducts =
  function () {

    return window.RISE_SKIN
      .getActiveRoutineProducts()
      .filter(
        (product) =>
          product?.commerce?.route ===
            "shopify" &&
          product?.commerce?.variantId &&
          product?.available !== false
      );
  };


// ============================================================
// KEEP RESULTS OPEN WHEN VIEWING CART
// ============================================================

window.RISE_SKIN.bindCartLinksNewTab =
  function () {

    const mobile = window.matchMedia?.("(max-width: 749px)")?.matches;

    document
      .querySelectorAll(
        'a[href="/cart"], a[href^="/cart?"]'
      )
      .forEach(
        (link) => {

          if (mobile) {
            // Keep mobile cart navigation in the same browser tab.
            // Back navigation can restore the completed plan below.
            link.removeAttribute("target");
            link.removeAttribute("rel");
            return;
          }

          link.setAttribute(
            "target",
            "_blank"
          );

          link.setAttribute(
            "rel",
            "noopener noreferrer"
          );
        }
      );
  };




window.RISE_SKIN.bindCartLinksNewTab();

window.addEventListener(
  "rise:skin-plan-unlocked",
  function () {
    window.RISE_SKIN
      .bindCartLinksNewTab();
  }
);

// ============================================================
// COMPLETE ROUTINE TOTAL
// ============================================================

window.RISE_SKIN.getActiveRoutineTotal =
  function () {

    const products =
      window.RISE_SKIN
        .getActiveRoutineCartProducts();

    return products.reduce(
      (sum, product) => {

        const price =
          Number(
            product?.shopifyData
              ?.price ??
            product?.price ??
            0
          );

        return (
          sum +
          (
            Number.isNaN(price)
              ? 0
              : price
          )
        );
      },
      0
    );
  };


// ============================================================
// COMPLETE ROUTINE CART + SHADE SELECTION
// ============================================================

(function () {
  "use strict";

  const skin = window.RISE_SKIN;
  const pendingKey = "rise-skin-pending-shades-v1";
  let addingRoutine = false;
  let previousFocus = null;

  function cartURL() {
    return window.routes?.cart_url || "/cart";
  }

  function productURL(handle) {
    return "/products/" + encodeURIComponent(handle);
  }

  function pendingShades() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(pendingKey) || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch (_) {
      return [];
    }
  }

  function savePending(products) {
    const records = products.map(product => ({
      id: String(product.id || ""),
      name: String(product.name || product.shopifyTitle || "Product"),
      handle: String(product.commerce?.productHandle || ""),
      productId: String(product.commerce?.productId || "")
    })).filter(item => item.handle);
    try {
      if (records.length) sessionStorage.setItem(pendingKey, JSON.stringify(records));
      else sessionStorage.removeItem(pendingKey);
    } catch (_) { /* A blocked storage API must not block shopping. */ }
  }

  async function getCart() {
    const response = await fetch(cartURL() + ".js", { credentials: "same-origin" });
    if (!response.ok) throw new Error("Could not check your cart.");
    return response.json();
  }

  async function addVariant(id) {
    const response = await fetch((window.routes?.cart_add_url || "/cart/add").replace(/\/$/, "") + ".js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    });
    if (!response.ok) {
      let detail = "Could not add this item. Please try again.";
      try {
        const data = await response.json();
        detail = String(data.description || data.message || detail);
      } catch (_) { /* Use a useful fallback. */ }
      const error = new Error(detail);
      error.status = response.status;
      throw error;
    }
  }

  // Use the full routine here: getActiveRoutineCartProducts excludes shade items.
  skin.addCompleteRoutineToCart = async function () {
    const result = {
      success: false, added: [], alreadyInCart: [], needsShade: [], failed: []
    };
    const products = skin.getActiveRoutineProducts();
    if (!products.length) return result;

    let existing = null;
    try { existing = await getCart(); }
    catch (_) { /* Individual adds still work if cart inspection fails. */ }
    const existingIds = new Set((existing?.items || []).map(item => String(item.variant_id || item.id)));
    const existingProductIds = new Set((existing?.items || []).map(item => String(item.product_id)));

    for (const product of products) {
      const commerce = product?.commerce || {};
      if (commerce.route !== "shopify") {
        result.failed.push({ product, reason: "Product is not ready for online checkout" });
        continue;
      }
      if (product.available === false) {
        result.failed.push({ product, reason: "Currently unavailable" });
        continue;
      }
      if (commerce.requiresVariantSelection) {
        if (commerce.productId && existingProductIds.has(String(commerce.productId))) {
          result.alreadyInCart.push(product);
        } else if (commerce.productHandle) {
          result.needsShade.push(product);
        } else {
          result.failed.push({ product, reason: "Missing product page" });
        }
        continue;
      }
      const variantId = Number(commerce.variantId || 0);
      if (!Number.isSafeInteger(variantId) || variantId < 1) {
        result.failed.push({ product, reason: "Missing Shopify variant ID" });
        continue;
      }
      if (existingIds.has(String(variantId))) {
        result.alreadyInCart.push(product);
        continue;
      }
      try {
        await addVariant(variantId);
        result.added.push(product);
        existingIds.add(String(variantId));
      } catch (error) {
        if (error.status === 422) {
          // A 422 can mean sold out OR a quantity limit. Verify before claiming it is in cart.
          try {
            const cart = await getCart();
            if (cart.items?.some(item => String(item.variant_id || item.id) === String(variantId))) {
              result.alreadyInCart.push(product);
              continue;
            }
          } catch (_) { /* Show the original error. */ }
        }
        result.failed.push({ product, reason: error.message });
      }
    }
    result.success = result.failed.length === 0 &&
      (result.added.length + result.alreadyInCart.length + result.needsShade.length > 0);
    savePending(result.needsShade);
    if (window.RISE_DEBUG) console.log("RISE SKIN routine cart result", result);
    return result;
  };

  function styles() {
    if (document.getElementById("rsc-shade-styles")) return;
    const style = document.createElement("style");
    style.id = "rsc-shade-styles";
    style.textContent = `
      .rsc-shade-overlay{position:fixed;inset:0;z-index:99999;background:rgba(28,30,27,.64);display:flex;align-items:center;justify-content:center;padding:20px;overflow:auto}
      .rsc-shade-dialog{position:relative;width:min(100%,540px);max-height:calc(100dvh - 40px);overflow:auto;background:#f8f6f0;color:#272b27;padding:clamp(24px,5vw,44px);box-shadow:0 16px 65px rgba(0,0,0,.2);font-family:inherit}
      .rsc-shade-dialog h2{font-size:clamp(24px,4vw,34px);line-height:1.2;margin:12px 35px 14px 0;color:#272b27}
      .rsc-shade-dialog p{font-size:16px;line-height:1.55;margin:0 0 20px}
      .rsc-shade-eyebrow{font-size:11px;letter-spacing:.19em;text-transform:uppercase;font-weight:700;color:#626c5e}
      .rsc-shade-close{position:absolute;top:14px;right:17px;border:0;background:transparent;color:#272b27;font-size:28px;line-height:1;cursor:pointer;padding:8px}
      .rsc-shade-actions{display:grid;gap:11px;margin-top:23px}
      .rsc-shade-primary,.rsc-shade-secondary{display:block;width:100%;box-sizing:border-box;padding:14px 18px;text-align:center;font:inherit;font-size:13px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;text-decoration:none;border:1px solid #354136;border-radius:0}
      .rsc-shade-primary{background:#354136;color:#fff}.rsc-shade-primary:hover{background:#4b5c4b;color:#fff}
      .rsc-shade-secondary{background:transparent;color:#354136}.rsc-shade-secondary:hover{background:#e9ede6;color:#354136}
      .rsc-shade-primary:disabled{opacity:.55;cursor:wait}
      .rsc-shade-dialog select{display:block;width:100%;min-height:48px;margin:12px 0;padding:10px;border:1px solid #878d82;background:#fff;color:#272b27;font:inherit}
      .rsc-shade-list{padding-left:20px;margin:0 0 14px;line-height:1.7}
      .rsc-shade-error{color:#923e32!important;font-weight:600}
      .rsc-shade-note{font-size:13px!important;color:#505b50}
      .rsc-cart-reminder{background:#f8f6f0;color:#272b27;border:1px solid #c7cec3;padding:18px 22px;margin:20px auto;max-width:900px;font:inherit}
      .rsc-cart-reminder h2{font-size:22px;margin:0 0 8px}.rsc-cart-reminder p{margin:0 0 12px}
      .rsc-cart-reminder a{display:inline-block;margin:0 16px 7px 0;color:#354136;text-decoration:underline}
      .rsc-shade-overlay :focus-visible,.rsc-cart-reminder :focus-visible{outline:2px solid #354136;outline-offset:3px}
      @media(max-width:600px){.rsc-shade-overlay{align-items:flex-end;padding:0}.rsc-shade-dialog{width:100%;max-height:90dvh;padding:28px 22px}}
    `;
    document.head.appendChild(style);
  }

  function el(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value != null) node.textContent = value;
    return node;
  }

  function closeModal(restoreFocus = true) {
    document.getElementById("rise-shade-selection-modal")?.remove();
    if (restoreFocus) {
      document.body.style.overflow = document.body.dataset.rscPriorOverflow || "";
      delete document.body.dataset.rscPriorOverflow;
      previousFocus?.focus?.();
      previousFocus = null;
    }
  }

  function makeModal() {
    const replacing = Boolean(document.getElementById("rise-shade-selection-modal"));
    if (replacing) closeModal(false);
    styles();
    if (!replacing) {
      previousFocus = document.activeElement;
      document.body.dataset.rscPriorOverflow = document.body.style.overflow;
    }
    document.body.style.overflow = "hidden";
    const overlay = el("div", "rsc-shade-overlay");
    overlay.id = "rise-shade-selection-modal";
    const dialog = el("div", "rsc-shade-dialog");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-label", "Complete your skincare routine");
    const close = el("button", "rsc-shade-close", "×");
    close.type = "button";
    close.setAttribute("aria-label", "Close shade selection");
    close.addEventListener("click", () => closeModal());
    dialog.appendChild(close);
    overlay.appendChild(dialog);
    overlay.addEventListener("click", event => { if (event.target === overlay) closeModal(); });
    overlay.addEventListener("keydown", event => {
      if (event.key === "Escape") { event.preventDefault(); closeModal(); }
      if (event.key === "Tab") {
        const controls = [...dialog.querySelectorAll("button:not(:disabled),a[href],select:not(:disabled)")];
        if (!controls.length) return;
        if (event.shiftKey && document.activeElement === controls[0]) {
          event.preventDefault(); controls[controls.length - 1].focus();
        } else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) {
          event.preventDefault(); controls[0].focus();
        }
      }
    });
    document.body.appendChild(overlay);
    close.focus();
    return dialog;
  }

  function actions(dialog, main, secondary) {
    const row = el("div", "rsc-shade-actions");
    if (main) row.appendChild(main);
    if (secondary) row.appendChild(secondary);
    dialog.appendChild(row);
  }

  function cartLink(label) {
    const link = el("a", "rsc-shade-secondary", label);
    link.href = cartURL();
    return link;
  }

  function showFinish(failed) {
    const dialog = makeModal();
    dialog.appendChild(el("div", "rsc-shade-eyebrow", "RISE SKIN CONCIERGE"));
    dialog.appendChild(el("h2", "", failed.length ? "Review your routine" : "Your full routine is in your cart!"));
    if (failed.length) {
      dialog.appendChild(el("p", "", "Some items could not be added. You can review them before checkout:"));
      const list = el("ul", "rsc-shade-list");
      failed.forEach(entry => list.appendChild(el("li", "", `${entry.product?.name || "Product"}: ${entry.reason}`)));
      dialog.appendChild(list);
    }
    actions(dialog, cartLink("VIEW CART →"));
  }

  async function loadShadeProduct(product) {
    const handle = product.commerce.productHandle;
    const cached = skin.SHOPIFY_PRODUCTS?.[handle] || skin.SHOPIFY_CACHE?.[handle];
    if (cached?.variants?.length) return cached;
    const response = await fetch(productURL(handle) + ".js", { credentials: "same-origin" });
    if (!response.ok) throw new Error("Could not load available shades. Please try again.");
    return response.json();
  }

  function shadeLabel(variant) {
    const name = String(variant.public_title || variant.title || "").trim();
    return name && name !== "Default Title" ? name : `Option ${variant.id}`;
  }

  async function showShadePicker(products, failed, index = 0) {
    if (index >= products.length) { savePending([]); showFinish(failed); return; }
    const product = products[index];
    const dialog = makeModal();
    dialog.appendChild(el("div", "rsc-shade-eyebrow", `SHADE ${index + 1} OF ${products.length}`));
    dialog.appendChild(el("h2", "", product.name || "Choose your shade"));
    const message = el("p", "", "Loading available shades…");
    dialog.appendChild(message);
    const status = el("p", "rsc-shade-error");
    status.setAttribute("role", "alert");
    dialog.appendChild(status);
    const fallback = el("a", "rsc-shade-secondary", "CHOOSE ON PRODUCT PAGE →");
    fallback.href = productURL(product.commerce.productHandle);
    fallback.target = "_blank";
    fallback.rel = "noopener noreferrer";
    const cart = cartLink("CONTINUE TO CART");
    actions(dialog, null, cart);
    try {
      const shopifyProduct = await loadShadeProduct(product);
      if (!dialog.isConnected) return;
      const variants = (shopifyProduct.variants || []).filter(variant => variant.available === true);
      if (!variants.length) throw new Error("There are no available shades for this product right now.");
      message.textContent = "Select your shade to add this product to your cart.";
      const select = el("select", "rsc-shade-select");
      select.setAttribute("aria-label", `Shade for ${product.name}`);
      const placeholder = el("option", "", "Choose a shade");
      placeholder.value = "";
      select.appendChild(placeholder);
      variants.forEach(variant => {
        const option = el("option", "", shadeLabel(variant));
        option.value = String(variant.id);
        select.appendChild(option);
      });
      message.after(select);
      const button = el("button", "rsc-shade-primary", "ADD SHADE TO CART →");
      button.type = "button";
      button.addEventListener("click", async () => {
        if (!select.value) { status.textContent = "Please choose a shade first."; select.focus(); return; }
        button.disabled = true;
        button.textContent = "ADDING SHADE…";
        status.textContent = "";
        try {
          // Avoid adding the same variant twice after an interrupted session.
          const cartState = await getCart();
          if (!cartState.items?.some(item => String(item.variant_id || item.id) === select.value)) {
            await addVariant(select.value);
          }
          savePending(products.slice(index + 1));
          await showShadePicker(products, failed, index + 1);
        } catch (error) {
          status.textContent = error.message || "Could not add this shade. Please try again.";
          button.disabled = false;
          button.textContent = "TRY AGAIN →";
        }
      });
      dialog.querySelector(".rsc-shade-actions").prepend(button);
      select.focus();
    } catch (error) {
      if (!dialog.isConnected) return;
      message.textContent = error.message;
      dialog.querySelector(".rsc-shade-actions").prepend(fallback);
    }
  }

  skin.showShadeSelectionNotice = function (products, failed = []) {
    const dialog = makeModal();
    const count = products.length;
    dialog.appendChild(el("div", "rsc-shade-eyebrow", "YOUR ROUTINE IS ALMOST READY"));
    dialog.appendChild(el("h2", "", `Just ${count} more ${count === 1 ? "step" : "steps"}.`));
    dialog.appendChild(el("p", "", `We added the ready products to your cart. Choose ${count === 1 ? "a shade" : "shades"} for ${count} ${count === 1 ? "product" : "products"} to complete your routine.`));
    const list = el("ul", "rsc-shade-list");
    products.forEach(product => list.appendChild(el("li", "", product.name)));
    dialog.appendChild(list);
    if (failed.length) dialog.appendChild(el("p", "rsc-shade-error", `${failed.length} other ${failed.length === 1 ? "product needs" : "products need"} review before checkout.`));
    const choose = el("button", "rsc-shade-primary", count === 1 ? "CHOOSE MY SHADE →" : "CHOOSE MY SHADES →");
    choose.type = "button";
    choose.addEventListener("click", () => showShadePicker(products, failed));
    actions(dialog, choose, cartLink("CONTINUE TO CART"));
  };

  window.rscCartDemo = async function () {
    if (addingRoutine) return;
    addingRoutine = true;
    const button = document.querySelector('[onclick*="rscCartDemo"]');
    const previousText = button?.textContent;
    if (button) { button.disabled = true; button.textContent = "ADDING PRODUCTS…"; }
    try {
      // The commerce map loads asynchronously after this script.
      if (!window.RISE_SKIN_COMMERCE_REPORT && skin.applyCommerceMap) await skin.applyCommerceMap();
      const result = await skin.addCompleteRoutineToCart();
      if (result.needsShade.length) {
        skin.showShadeSelectionNotice(result.needsShade, result.failed);
        if (button) button.textContent = "FINISH MY ROUTINE →";
      } else if (result.failed.length) {
        showFinish(result.failed);
        if (button) button.textContent = "REVIEW CART ITEMS →";
      } else if (result.added.length) {
        if (button) button.textContent = "ADDED TO CART ✓";
      } else if (result.alreadyInCart.length) {
        if (button) button.textContent = "ALREADY IN CART ✓";
      } else {
        if (button) button.textContent = "NO PRODUCTS READY — TRY AGAIN";
      }
    } catch (error) {
      console.error("RISE SKIN: Could not add complete routine", error);
      if (button) button.textContent = "TRY AGAIN";
      const dialog = makeModal();
      dialog.appendChild(el("h2", "", "We couldn't finish adding your routine"));
      dialog.appendChild(el("p", "rsc-shade-error", "Please try again. Your cart may contain some of your products."));
      actions(dialog, cartLink("REVIEW CART →"));
    } finally {
      addingRoutine = false;
      if (button) button.disabled = false;
      if (button && !button.textContent) button.textContent = previousText;
    }
  };

  // Results JS is included on every page; show skipped shade items above the cart.
  async function showCartReminder() {
    if (!/^\/cart\/?$/.test(location.pathname) || document.getElementById("rsc-cart-reminder")) return;
    let remaining = pendingShades();
    if (!remaining.length) return;
    try {
      const cart = await getCart();
      const productIds = new Set((cart.items || []).map(item => String(item.product_id)));
      remaining = remaining.filter(item => !item.productId || !productIds.has(item.productId));
      if (remaining.length !== pendingShades().length) {
        // Preserve only the items that are still missing from the cart.
        try { sessionStorage.setItem(pendingKey, JSON.stringify(remaining)); } catch (_) {}
      }
    } catch (_) { /* Continue displaying the saved reminder. */ }
    if (!remaining.length) { savePending([]); return; }
    styles();
    const banner = el("section", "rsc-cart-reminder");
    banner.id = "rsc-cart-reminder";
    banner.setAttribute("aria-label", "Complete your routine");
    banner.appendChild(el("h2", "", "Complete Your Routine"));
    banner.appendChild(el("p", "", `${remaining.length} recommended ${remaining.length === 1 ? "product still needs" : "products still need"} a shade before it can be added.`));
    remaining.forEach(item => {
      const link = el("a", "", `Choose shade + add — ${item.name} →`);
      link.href = productURL(item.handle);
      banner.appendChild(link);
    });
    const main = document.querySelector("main#MainContent") || document.querySelector("main");
    if (main) main.prepend(banner);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", showCartReminder);
  else showCartReminder();
})();


// ============================================================
// SUBSCRIPTION-READY STATE
//
// Shopify has not approved selling plans yet.
// These helpers stay dormant until Shopify returns plans.
// ============================================================

window.RISE_SKIN.getSellingPlans =
  function (product) {

    const groups =
      product?.shopifyData
        ?.sellingPlanGroups ||
      product?.sellingPlanGroups ||
      [];

    if (
      !Array.isArray(groups)
    ) {
      return [];
    }


    return groups.flatMap(
      (group) =>
        Array.isArray(
          group?.selling_plans
        )
          ? group.selling_plans
          : Array.isArray(
              group?.sellingPlans
            )
            ? group.sellingPlans
            : []
    );
  };


window.RISE_SKIN.productHasSubscription =
  function (product) {

    return (
      window.RISE_SKIN
        .getSellingPlans(product)
        .length > 0
    );
  };


// ============================================================
// FUTURE PURCHASE STATE
//
// This object will eventually hold:
// one-time vs subscription + selected frequency.
// ============================================================

window.RISE_SKIN.PURCHASE_OPTIONS =
  window.RISE_SKIN
    .PURCHASE_OPTIONS ||
  {};


window.RISE_SKIN.getPurchaseOption =
  function (productId) {

    return (
      window.RISE_SKIN
        .PURCHASE_OPTIONS[
          productId
        ] ||
      {
        type: "one_time",
        sellingPlanId: null
      }
    );
  };


window.RISE_SKIN.setPurchaseOption =
  function (
    productId,
    option
  ) {

    if (!productId) {
      return;
    }

    window.RISE_SKIN
      .PURCHASE_OPTIONS[
        productId
      ] = {
        ...window.RISE_SKIN
          .getPurchaseOption(
            productId
          ),

        ...option
      };
  };


// ============================================================
// DEBUG
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Complete Routine Cart + Subscription Framework loaded"
  );

}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Results Renderer — Part 7
 * Shopify Forms Results Gate
 * Form ID: 1133407
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// SHOPIFY FORM GATE STATE
// ============================================================

window.RISE_SKIN.FORM_GATE = {
  unlocked: false,
  formHost: null,
  formContainer: null,
  successObserver: null,
  visibilityObserver: null,
  shouldBeVisible: false
};


// ============================================================
// FIND SHOPIFY FORM
// ============================================================

window.RISE_SKIN.findSkinConciergeForm =
  function () {

    const concierge =
      document.getElementById(
        "rise-skin-concierge"
      );

    // First choice: locate the Shopify Forms section structurally
    // from the Concierge section. This does not depend on shadow-DOM
    // text or Shopify form state.
    if (concierge) {

      const conciergeSection =
        concierge.closest(
          ".shopify-section"
        ) ||
        concierge.parentElement;

      let sibling =
        conciergeSection?.nextElementSibling ||
        concierge.nextElementSibling;

      while (sibling) {

        const host =
          sibling.matches?.(
            "shopify-forms-embed"
          )
            ? sibling
            : sibling.querySelector?.(
                "shopify-forms-embed"
              );

        if (host) {

          const container =
            sibling.matches?.(
              ".shopify-section"
            )
              ? sibling
              : host.closest(
                  ".shopify-section"
                ) || sibling;

          return {
            host,
            container
          };
        }

        // Stop if we reach another major Concierge-like section
        // without finding the form.
        sibling = sibling.nextElementSibling;
      }
    }

    // Fallback: use the first Shopify Forms embed on the page,
    // but still hide/show its entire Shopify section.
    const host =
      document.querySelector(
        "shopify-forms-embed"
      );

    if (!host) {
      return null;
    }

    const container =
      host.closest(
        ".shopify-section"
      ) ||
      host.parentElement;

    if (!container) {
      return null;
    }

    return {
      host,
      container
    };
  };
// ============================================================
// HIDE SHOPIFY FORM
// ============================================================

window.RISE_SKIN.hideSkinConciergeForm =
  function () {

    const form =
      window.RISE_SKIN
        .findSkinConciergeForm();


    if (!form) {
      return false;
    }


    form.container.classList.add(
      "rise-skin-lead-gate-hidden"
    );

    // Force-hide the Shopify form independently of theme CSS.
    form.container.style.setProperty(
      "display",
      "none",
      "important"
    );


    window.RISE_SKIN
      .FORM_GATE
      .formHost =
      form.host;


    window.RISE_SKIN
      .FORM_GATE
      .formContainer =
      form.container;


    return true;
  };


// ============================================================
// SHOPIFY FORM SUCCESS STATE
// ============================================================

window.RISE_SKIN.hasSkinConciergeFormSucceeded =
  function (host, container) {

    // Shopify Forms can render its success message inside several
    // nested open shadow roots. textContent on the outer host does
    // not include text from those inner roots.
    const seen = new Set();
    const parts = [];

    const collect = function (node) {
      if (!node || seen.has(node)) return;
      seen.add(node);
      parts.push(node.textContent || "");

      if (node.shadowRoot) collect(node.shadowRoot);
      node.querySelectorAll?.("*").forEach(function (element) {
        if (element.shadowRoot) collect(element.shadowRoot);
      });
    };

    collect(host);
    collect(container);

    const text = parts.join(" ");

    return (
      text.includes(
        "Your plan is ready!"
      ) ||
      text.includes(
        "Revealing your personalized Rise skincare routine now"
      )
    );
  };


// ============================================================
// IS LEAD FORM CURRENTLY ALLOWED?
// ============================================================

window.RISE_SKIN.isSkinConciergeLeadGateAllowed =
  function () {

    const buildingScreen =
      document.getElementById(
        "rsc-building"
      );

    const buildingIsActive =
      Boolean(
        buildingScreen &&
        buildingScreen.classList.contains(
          "active"
        )
      );

    const hasCompletedResult =
      Boolean(
        window.RISE_SKIN.RESULTS?.data
      );

    const alreadyUnlocked =
      Boolean(
        window.RISE_SKIN.FORM_GATE?.unlocked
      );

    return (
      buildingIsActive &&
      hasCompletedResult &&
      !alreadyUnlocked
    );
  };


// ============================================================
// SHOW SHOPIFY FORM
// ============================================================

window.RISE_SKIN.showSkinConciergeForm =
  function () {

    // Never allow the lead form to appear while INTRO, QUIZ,
    // or RESULTS is active. It can only open from the completed
    // BUILDING state with a real result already generated.
    if (
      !window.RISE_SKIN
        .isSkinConciergeLeadGateAllowed()
    ) {

      window.RISE_SKIN
        .FORM_GATE
        .shouldBeVisible =
        false;

      window.RISE_SKIN
        .hideSkinConciergeForm();

      return false;
    }


    let host =
      window.RISE_SKIN
        .FORM_GATE
        .formHost;

    let container =
      window.RISE_SKIN
        .FORM_GATE
        .formContainer;


    // If we did not cache the form earlier,
    // try to locate it again.
    if (!host || !container) {

      const form =
        window.RISE_SKIN
          .findSkinConciergeForm();

      if (form) {

        host =
          form.host;

        container =
          form.container;


        window.RISE_SKIN
          .FORM_GATE
          .formHost =
          host;

        window.RISE_SKIN
          .FORM_GATE
          .formContainer =
          container;
      }
    }


    if (!host || !container) {

      console.warn(
        "RISE SKIN: Shopify Skin Concierge form not ready yet."
      );

      return false;
    }


    // Returning/test users may already be in Shopify's success state.
    // If so, do not flash the success message. Treat the gate as satisfied
    // and reveal the personalized results immediately.
    if (
      window.RISE_SKIN
        .hasSkinConciergeFormSucceeded(
          host,
          container
        )
    ) {

      window.RISE_SKIN
        .FORM_GATE
        .shouldBeVisible =
        false;

      container.classList.add(
        "rise-skin-lead-gate-hidden"
      );

      container.style.setProperty(
        "display",
        "none",
        "important"
      );

      window.setTimeout(
        function () {
          window.RISE_SKIN
            .revealSkinConciergeResults();
        },
        50
      );

      return true;
    }


    window.RISE_SKIN
      .FORM_GATE
      .shouldBeVisible =
      true;


    container.classList.remove(
      "rise-skin-lead-gate-hidden"
    );

    // Reveal only after quiz completion.
    container.style.removeProperty(
      "display"
    );


    const shell =
      document.querySelector(
        "#rise-skin-concierge .rsc-shell"
      );

    if (shell) {
      shell.classList.add(
        "rise-lead-gate-active"
      );
    }


    container.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });


    window.RISE_SKIN
      .watchSkinConciergeFormSuccess();


    return true;
  };
// ============================================================
// REVEAL RESULTS
// ============================================================

window.RISE_SKIN.revealSkinConciergeResults =
  function () {

    if (
      window.RISE_SKIN
        .FORM_GATE
        .unlocked
    ) {
      return;
    }


    const result =
      window.RISE_SKIN.RESULTS?.data;


    if (!result) {
      return;
    }


    window.RISE_SKIN
      .FORM_GATE
      .unlocked =
      true;


    window.RISE_SKIN
      .FORM_GATE
      .shouldBeVisible =
      false;


    // Hide Shopify form after successful capture.
    if (
      window.RISE_SKIN
        .FORM_GATE
        .formContainer
    ) {

     window.RISE_SKIN
  .FORM_GATE
  .formContainer
  .classList.add(
    "rise-skin-lead-gate-hidden"
  );

     window.RISE_SKIN
       .FORM_GATE
       .formContainer
       .style
       .setProperty(
         "display",
         "none",
         "important"
       );
    }


    // Populate existing personalized results.
    window.RISE_SKIN
      .populateResults(
        result
      );


    window.RISE_SKIN
      .showConciergeScreen(
        "rsc-results"
      );


    const concierge =
      document.getElementById(
        "rise-skin-concierge"
      );


    if (concierge) {

      concierge.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }


    window.dispatchEvent(
      new CustomEvent(
        "rise:skin-plan-unlocked"
      )
    );
  };


// ============================================================
// WATCH SHOPIFY FORM FOR SUCCESS
// ============================================================

window.RISE_SKIN.watchSkinConciergeFormSuccess =
  function () {

    const host =
      window.RISE_SKIN
        .FORM_GATE
        .formHost;

    const container =
      window.RISE_SKIN
        .FORM_GATE
        .formContainer;


    if (!host || !container) {
      return;
    }


    if (
      window.RISE_SKIN
        .FORM_GATE
        .successObserver
    ) {

      window.RISE_SKIN
        .FORM_GATE
        .successObserver
        .disconnect();
    }

    if (window.RISE_SKIN.FORM_GATE.successTimer) {
      window.clearInterval(
        window.RISE_SKIN.FORM_GATE.successTimer
      );
      window.RISE_SKIN.FORM_GATE.successTimer = null;
    }


    // Shopify Forms exposes its current form state through a
    // data-current-step attribute inside the open shadow DOM.
    // We use that state change as the primary success signal,
    // with accessible success text as a secondary fallback.
    const getOpenRoots =
      function (root) {

        const roots = [];
        const seen = new Set();

        const visit =
          function (node) {

            if (!node || seen.has(node)) {
              return;
            }

            seen.add(node);
            roots.push(node);

            const elements =
              node.querySelectorAll
                ? Array.from(
                    node.querySelectorAll("*")
                  )
                : [];

            elements.forEach(
              function (element) {
                if (element.shadowRoot) {
                  visit(element.shadowRoot);
                }
              }
            );
          };

        visit(root);
        return roots;
      };


    const getStepElements =
      function () {

        const roots = [
          ...getOpenRoots(host),
          ...getOpenRoots(container)
        ];

        const elements = [];
        const seen = new Set();

        roots.forEach(
          function (root) {

            if (
              root.nodeType === 1 &&
              root.hasAttribute?.(
                "data-current-step"
              ) &&
              !seen.has(root)
            ) {
              seen.add(root);
              elements.push(root);
            }

            root.querySelectorAll?.(
              "[data-current-step]"
            ).forEach(
              function (element) {
                if (!seen.has(element)) {
                  seen.add(element);
                  elements.push(element);
                }
              }
            );
          }
        );

        return elements;
      };


    const getStepValues =
      function () {

        return getStepElements()
          .map(
            function (element) {
              return String(
                element.getAttribute(
                  "data-current-step"
                ) || ""
              ).trim();
            }
          )
          .filter(Boolean);
      };


    const baselineSteps =
      getStepValues();


    let revealed = false;

    const revealOnce =
      function () {

        if (revealed) {
          return true;
        }

        revealed = true;

        window.RISE_SKIN
          .FORM_GATE
          .successObserver
          ?.disconnect();

        window.clearInterval(
          window.RISE_SKIN.FORM_GATE.successTimer
        );
        window.RISE_SKIN.FORM_GATE.successTimer = null;

        window.setTimeout(
          function () {
            window.RISE_SKIN
              .revealSkinConciergeResults();
          },
          350
        );

        return true;
      };


    const stepLooksComplete =
      function (value) {

        const normalized =
          String(value || "")
            .trim()
            .toLowerCase();

        if (!normalized) {
          return false;
        }

        if (
          /success|complete|completed|submitted|thank|done|confirmation/.test(
            normalized
          )
        ) {
          return true;
        }

        // Shopify's single-step embedded form commonly advances
        // from step 0 to a later numeric step after submission.
        const numeric = Number(normalized);

        return (
          Number.isFinite(numeric) &&
          numeric > 0
        );
      };


    const detectSuccess =
      function () {

        if (revealed) {
          return true;
        }

        // Existing text-based detection remains useful when the
        // success copy is accessible through an open shadow root.
        if (
          window.RISE_SKIN
            .hasSkinConciergeFormSucceeded(
              host,
              container
            )
        ) {
          return revealOnce();
        }


        const currentSteps =
          getStepValues();

        if (
          currentSteps.some(
            stepLooksComplete
          )
        ) {
          return revealOnce();
        }


        // If Shopify changes the step value from the state that
        // existed when the lead gate opened, the form has advanced.
        if (
          baselineSteps.length &&
          currentSteps.length &&
          currentSteps.some(
            function (value) {
              return !baselineSteps.includes(
                value
              );
            }
          )
        ) {
          return revealOnce();
        }


        return false;
      };


    const observer =
      new MutationObserver(
        detectSuccess
      );


    // Observe the outer Shopify section.
    observer.observe(
      container,
      {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          "data-current-step"
        ]
      }
    );


    // Observe any currently accessible shadow roots and step nodes.
    getOpenRoots(host).forEach(
      function (root) {
        try {
          observer.observe(
            root,
            {
              childList: true,
              subtree: true,
              characterData: true,
              attributes: true,
              attributeFilter: [
                "data-current-step"
              ]
            }
          );
        } catch (error) {
          // Some roots may not be observable; polling below covers them.
        }
      }
    );


    window.RISE_SKIN
      .FORM_GATE
      .successObserver =
      observer;


    if (detectSuccess()) {
      return;
    }


    // Keep checking while the form is open. Customers may need
    // much longer than 30 seconds to complete it on a phone, and
    // mutations inside a later-mounted shadow root do not reach
    // the observer attached to the outer section.
    window.RISE_SKIN.FORM_GATE.successTimer =
      window.setInterval(function () {
        if (
          window.RISE_SKIN.FORM_GATE.unlocked ||
          !window.RISE_SKIN.FORM_GATE.shouldBeVisible
        ) {
          window.clearInterval(
            window.RISE_SKIN.FORM_GATE.successTimer
          );
          window.RISE_SKIN.FORM_GATE.successTimer = null;
          return;
        }
        detectSuccess();
      }, 750);
  };


// ============================================================
// WAIT FOR SHOPIFY FORMS TO LOAD
//
// Shopify Forms loads asynchronously.
// Hide it as soon as it appears.
// ============================================================

window.RISE_SKIN.initializeSkinConciergeFormGate =
  function () {

    const enforceHiddenState =
      function () {

        const gateAllowed =
          window.RISE_SKIN
            .isSkinConciergeLeadGateAllowed();

        const explicitlyVisible =
          Boolean(
            window.RISE_SKIN
              .FORM_GATE
              .shouldBeVisible
          );

        // The form stays hidden unless BOTH the current screen
        // is the completed BUILDING state and showSkinConciergeForm
        // has explicitly opened the gate.
        if (
          gateAllowed &&
          explicitlyVisible
        ) {
          return;
        }

        window.RISE_SKIN
          .FORM_GATE
          .shouldBeVisible =
          false;

        window.RISE_SKIN
          .hideSkinConciergeForm();
      };


    // Hide immediately if Shopify Forms is already mounted.
    enforceHiddenState();


    // Shopify Forms can replace/re-render its DOM after page load.
    // Keep the form hidden until the quiz explicitly requests it.
    if (
      window.RISE_SKIN
        .FORM_GATE
        .visibilityObserver
    ) {
      window.RISE_SKIN
        .FORM_GATE
        .visibilityObserver
        .disconnect();
    }


    const observer =
      new MutationObserver(
        enforceHiddenState
      );


    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );


    window.RISE_SKIN
      .FORM_GATE
      .visibilityObserver =
      observer;


    // Also poll briefly during Shopify's asynchronous app startup.
    let attempts = 0;

    const timer =
      window.setInterval(
        function () {

          attempts += 1;
          enforceHiddenState();

          if (attempts >= 200) {
            window.clearInterval(timer);
          }
        },
        100
      );
  };

// ============================================================
// OVERRIDE QUIZ COMPLETION
// ============================================================

window.RISE_SKIN.onQuizComplete =
  function (profile) {

    window.RISE_SKIN
      .FORM_GATE
      .shouldBeVisible =
      false;



    // Existing intentional building screen.
    window.RISE_SKIN
      .showConciergeScreen(
        "rsc-building"
      );


    // Build complete personalized plan invisibly.
    const result =
      window.RISE_SKIN
        .buildRoutines(
          profile
        );


    // Preserve result for reveal after Shopify capture.
    window.RISE_SKIN
      .RESULTS
      .data =
      result;


    window.RISE_SKIN
      .FORM_GATE
      .unlocked =
      false;


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN — RESULTS READY / WAITING FOR LEAD"
      );

      console.log(
        result
      );

      console.groupEnd();
    }


   window.setTimeout(
  function () {

    let attempts = 0;

    const revealForm =
      function () {

        attempts += 1;


        const shown =
          window.RISE_SKIN
            .showSkinConciergeForm();


        if (shown) {
          return;
        }


        if (attempts < 20) {

          window.setTimeout(
            revealForm,
            250
          );

        } else {

          console.error(
            "RISE SKIN: Shopify lead form could not be displayed."
          );
        }
      };


    revealForm();

  },
  900
);
  };


// ============================================================
// INITIALIZE
// ============================================================

window.RISE_SKIN
  .initializeSkinConciergeFormGate();


if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Shopify Forms Gate loaded"
  );
}

/* ============================================================
   RISE SKIN CONCIERGE — TREATMENT MATCHING ENGINE
   ============================================================ */

window.RISE_SKIN = window.RISE_SKIN || {};


/* ------------------------------------------------------------
   TREATMENT DEFINITIONS
   ------------------------------------------------------------ */

window.RISE_SKIN.TREATMENT_CATALOG = [

  {
    id: "bbl-heroic",
    name: "BBL HEROic",
    reason:
      "A strong professional option for visible pigment, redness, and sun-related skin changes.",
    weights: {
      pigment: 5,
      redness: 5,
      vascular: 5,
      dullness: 2
    },
    providerReview: true
  },

  {
    id: "moxi",
    name: "MOXI",
    reason:
      "A resurfacing treatment that can complement your plan for texture, pores, early lines, and overall tone.",
    weights: {
      pores: 5,
      texture: 5,
      fineLines: 4,
      dullness: 5,
      pigment: 3
    },
    providerReview: true
  },

  {
    id: "skinpen",
    name: "SkinPen Microneedling",
    reason:
      "A collagen-remodeling option for texture, pores, fine lines, and post-acne skin-quality concerns.",
    weights: {
      pores: 4,
      texture: 5,
      fineLines: 4,
      pigment: 2
    },
    providerReview: true
  },

  {
    id: "hydrafacial",
    name: "Hydrafacial",
    reason:
      "A low-downtime option for hydration, congestion, radiance, and an immediate refreshed look.",
    weights: {
      dullness: 5,
      dehydration: 5,
      congestion: 4,
      pores: 2
    },
    providerReview: false
  },


  {
    id: "biorepeel",
    name: "BioRePeel",
    reason:
      "A professional exfoliation option for uneven tone, texture, congestion, and visible dullness.",
    weights: {
      pigment: 4,
      texture: 4,
      congestion: 3,
      fineLines: 3,
      acne: 2
    },
    providerReview: true
  },

  {
    id: "morpheus8",
    name: "Morpheus8 / RF Microneedling",
    reason:
      "A more corrective collagen-remodeling option when firmness, laxity, texture, or deeper skin changes are priorities.",
    weights: {
      laxity: 5,
      texture: 3,
      fineLines: 4
    },
    providerReview: true
  },

  {
    id: "neuromodulator",
    name: "Botox / Dysport Consultation",
    reason:
      "Expression-driven lines respond differently than surface skin changes, so a neuromodulator consultation may be the best next step.",
    weights: {
      dynamicLines: 6,
      fineLines: 1
    },
    providerReview: true
  },

  {
    id: "bio-gel-prf",
    name: "Bio-Gel PRF",
    reason:
      "A regenerative option when under-eye hollowing or subtle volume loss is contributing more than surface skin quality alone.",
    weights: {
      underEye: 5,
      volumeLoss: 5,
      fineLines: 2
    },
    providerReview: true
  },

  {
    id: "sculptra",
    name: "Sculptra / Biostimulator Consultation",
    reason:
      "A collagen-stimulating option when firmness, diffuse volume loss, and structural aging are larger contributors.",
    weights: {
      laxity: 5,
      volumeLoss: 5,
      fineLines: 3
    },
    providerReview: true
  },

  {
    id: "facial-balancing",
    name: "Facial Balancing Consultation",
    reason:
      "Because some of your concerns appear structural, an in-person facial balancing assessment may offer more value than treating each concern separately.",
    weights: {
      volumeLoss: 6,
      laxity: 4,
      dynamicLines: 2
    },
    providerReview: true
  }

];


/* ------------------------------------------------------------
   SCORE ONE TREATMENT
   ------------------------------------------------------------ */

window.RISE_SKIN.scoreTreatment =
  function (treatment, result) {

    const scores =
      result?.scores || {};

    const profile =
      result?.profile || {};

    let total = 0;


    Object.entries(
      treatment.weights || {}
    ).forEach(
      ([key, weight]) => {

        total +=
          Number(scores[key] || 0) *
          Number(weight || 0);
      }
    );


    /* --------------------------------------------------------
       STRONG RULE BOOSTS
       -------------------------------------------------------- */

    if (
      treatment.id === "bbl-heroic" &&
      Number(scores.pigment || 0) >= 4 &&
      (
        Number(scores.redness || 0) >= 3 ||
        Number(scores.vascular || 0) >= 3
      )
    ) {
      total += 30;
    }


    if (
      treatment.id === "moxi" &&
      (
        Number(scores.pores || 0) >= 4 ||
        Number(scores.texture || 0) >= 4
      ) &&
      Number(scores.acne || 0) < 5
    ) {
      total += 20;
    }


    if (
      treatment.id === "neuromodulator" &&
      Number(scores.dynamicLines || 0) >= 4
    ) {
      total += 35;
    }


    if (
      treatment.id === "bio-gel-prf" &&
      (
        profile.eyeConcerns || []
      ).includes("hollowing")
    ) {
      total += 35;
    }


    const structuralCount = [
      Number(scores.volumeLoss || 0) >= 4,
      Number(scores.laxity || 0) >= 4,
      Number(scores.dynamicLines || 0) >= 4
    ].filter(Boolean).length;


    if (
      treatment.id ===
        "facial-balancing" &&
      structuralCount >= 2
    ) {
      total += 40;
    }


    /* --------------------------------------------------------
       SAFETY / CLINICAL SUPPRESSION
       -------------------------------------------------------- */

    if (
      profile.barrierStatus ===
        "impaired" &&
      [
        "bbl-heroic",
        "moxi",
        "skinpen",
        "biorepeel",
        "morpheus8"
      ].includes(treatment.id)
    ) {
      total -= 1000;
    }


    if (
      [
        "inflamed",
        "cystic"
      ].includes(
        profile.acneSeverity
      ) &&
      [
        "moxi",
        "skinpen",
        "biorepeel",
        "morpheus8"
      ].includes(treatment.id)
    ) {
      total -= 500;
    }


    if (
      [
        "high",
        "very_high"
      ].includes(
        profile.sunExposure
      ) &&
      [
        "bbl-heroic",
        "moxi"
      ].includes(treatment.id)
    ) {
      total -= 100;
    }


    return {
      ...treatment,
      score: total
    };
  };


/* ------------------------------------------------------------
   BUILD TREATMENT MATCHES
   ------------------------------------------------------------ */

window.RISE_SKIN.buildTreatmentMatches =
  function (result) {

    if (!result) {
      return {
        primary: null,
        secondary: null,
        ranked: []
      };
    }


    const profile =
      result.profile || {};


    /* --------------------------------------------------------
       IMPAIRED BARRIER OVERRIDE
       -------------------------------------------------------- */

    if (
      profile.barrierStatus ===
      "impaired"
    ) {

      return {
        primary: {
          id: "barrier-review",
          name:
            "Skin Recovery Consultation",
          reason:
            "Your skin is showing signs that barrier recovery should come before aggressive corrective treatments.",
          score: 999
        },

secondary: null,

        ranked: []
      };
    }


    /* --------------------------------------------------------
       SEVERE ACTIVE ACNE OVERRIDE
       -------------------------------------------------------- */

    if (
      [
        "inflamed",
        "cystic"
      ].includes(
        profile.acneSeverity
      )
    ) {

      return {
        primary: {
          id: "acne-consult",
          name:
            "Rise Acne Consultation",
          reason:
            "Because active inflammatory acne needs an individualized plan, an acne-focused consultation should come before elective resurfacing.",
          score: 999
        },

        secondary: null,

        ranked: []
      };
    }


    const ranked =
      window.RISE_SKIN
        .TREATMENT_CATALOG
        .map(
          treatment =>
            window.RISE_SKIN
              .scoreTreatment(
                treatment,
                result
              )
        )
        .filter(
          treatment =>
            treatment.score > 0
        )
        .sort(
          (a, b) =>
            b.score - a.score
        );


    return {
      primary:
        ranked[0] || null,

      secondary:
        ranked[1] || null,

      ranked
    };
  };


/* ------------------------------------------------------------
   ATTACH TREATMENTS TO BUILD RESULT
   ------------------------------------------------------------ */

window.RISE_SKIN
  .buildRoutinesBeforeTreatmentMatching =
    window.RISE_SKIN.buildRoutines;


window.RISE_SKIN.buildRoutines =
  function (profileInput) {

    const result =
      window.RISE_SKIN
        .buildRoutinesBeforeTreatmentMatching(
          profileInput
        );


    result.treatments =
      window.RISE_SKIN
        .buildTreatmentMatches(
          result
        );


    return result;
  };


if (window.RISE_DEBUG) {
  console.log(
    "RISE SKIN CONCIERGE — Treatment Matching Engine loaded"
  );
}
// ============================================================
// SHOPIFY COMPLETION BRIDGE COMPATIBILITY
// ============================================================
// rise-skin-shopify.js delegates quiz completion to this named
// pre-Shopify handler. Keep it pointed at the final Results/Form
// Gate completion handler defined above.
window.RISE_SKIN.onQuizCompleteBeforeShopify =
  window.RISE_SKIN.onQuizComplete;

// ============================================================
// RESTORE A COMPLETED PLAN AFTER MOBILE TAB RELOAD
// ============================================================
// Save only after the lead form succeeds. Mobile browsers may
// discard the quiz tab while an external booking tab is open.
// sessionStorage survives a reload of the original tab without
// keeping the visitor's quiz profile after the tab is closed.
(function () {
  "use strict";

  const skin = window.RISE_SKIN;
  const storageKey = "rise-skin-completed-plan-v1";
  const maxAgeMs = 24 * 60 * 60 * 1000;

  function clearSavedPlan() {
    try { sessionStorage.removeItem(storageKey); } catch (_) {}
  }

  function saveCompletedPlan() {
    const profile = skin.RESULTS?.data?.profile;
    if (!skin.FORM_GATE?.unlocked || !profile) return;

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        savedAt: Date.now(),
        path: location.pathname,
        level: skin.RESULTS.activeLevel || "complete",
        profile: profile
      }));
    } catch (error) {
      if (window.RISE_DEBUG) console.warn("RISE SKIN: Could not save plan", error);
    }
  }

  skin.clearSavedRoutine = clearSavedPlan;

  const reveal = skin.revealSkinConciergeResults;
  skin.revealSkinConciergeResults = function () {
    reveal.apply(this, arguments);
    saveCompletedPlan();
  };

  const setRoutine = window.rscSetRoutine;
  window.rscSetRoutine = function () {
    setRoutine.apply(this, arguments);
    saveCompletedPlan();
  };

  // A deliberate new quiz replaces the earlier completed plan.
  document.addEventListener("click", function (event) {
    if (event.target.closest?.('[onclick*="rscStartDynamicQuiz"]')) {
      clearSavedPlan();
    }
  }, true);

  function restoreCompletedPlan() {
    if (!document.getElementById("rsc-results") || skin.RESULTS?.data) return;

    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(storageKey) || "null"); }
    catch (_) { clearSavedPlan(); return; }

    if (!saved) return;
    if (
      saved.path !== location.pathname ||
      !Number.isFinite(saved.savedAt) ||
      Date.now() - saved.savedAt > maxAgeMs ||
      Date.now() < saved.savedAt ||
      !saved.profile ||
      typeof skin.buildRoutines !== "function"
    ) {
      clearSavedPlan();
      return;
    }

    try {
      // Rebuild from current product data instead of keeping stale
      // prices or Shopify variant details in storage.
      const result = skin.buildRoutines(saved.profile);
      if (!result?.routines) throw new Error("No routine available");

      skin.RESULTS.data = result;
      skin.FORM_GATE.unlocked = true;
      skin.FORM_GATE.shouldBeVisible = false;
      skin.hideSkinConciergeForm();
      skin.populateResults(result);
      skin.showConciergeScreen("rsc-results");

      if (result.routines[saved.level]) skin.renderRoutineLevel(saved.level);
      window.dispatchEvent(new CustomEvent("rise:skin-plan-unlocked"));
    } catch (error) {
      clearSavedPlan();
      if (window.RISE_DEBUG) console.warn("RISE SKIN: Could not restore plan", error);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", restoreCompletedPlan, { once: true });
  } else {
    restoreCompletedPlan();
  }
})();
